import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import "./styles.css";
import {
  GiDeer, GiBearFace, GiDuck, GiGoose, GiDove, GiRabbit, GiSquirrel,
  GiBoar, GiRam, GiGoat, GiFoxHead, GiWolfHead, GiLynxHead, GiRaccoonHead,
  GiCrocJaws, GiBison, GiEagleHead, GiPawPrint, GiPig, GiSparrow, GiTicket,
  GiRooster, GiStagHead, GiDeerHead, GiBeaver,
} from "react-icons/gi";

// ═══════════════════════════════════════════
// LOCAL STORAGE PERSISTENCE
// ═══════════════════════════════════════════

const STORAGE_KEYS = {
  activeState: "tagvault_activeState",
  hunts: "tagvault_hunts2",      // flat array of hunt entry objects
  resident: "tagvault_resident", // JSON object keyed by state code
  calendarView: "tagvault_calendarView", // { year, month }
  lastTab: "tagvault_lastTab",   // last active tab
};

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

function saveToStorage(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ═══════════════════════════════════════════
// STATE DATA LOADING — LAZY FETCH + CACHE
// ═══════════════════════════════════════════

const SUPPORTED_STATES = {
  AL: { name: "Alabama", color: "#9E1B32" },
  AK: { name: "Alaska", color: "#003366" },
  CO: { name: "Colorado", color: "#1D428A" },
  MT: { name: "Montana", color: "#004B49" },
  TX: { name: "Texas", color: "#BF5700" },
  UT: { name: "Utah", color: "#BE0000" },
  WI: { name: "Wisconsin", color: "#C5050C" },
  WY: { name: "Wyoming", color: "#6B3A2A" },
};

const ALL_STATES_LIST = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

// In-memory cache for fetched state data
const stateDataCache = {};

async function loadStateData(stateCode) {
  if (stateDataCache[stateCode]) return stateDataCache[stateCode];
  const resp = await fetch(`${process.env.PUBLIC_URL}/data/${stateCode}.json`);
  if (!resp.ok) throw new Error(`Failed to load ${stateCode} data`);
  const data = await resp.json();
  stateDataCache[stateCode] = data;
  return data;
}

function useStateData(stateCode) {
  const [data, setData] = useState(stateDataCache[stateCode] || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!stateCode) { setData(null); return; }
    if (stateDataCache[stateCode]) { setData(stateDataCache[stateCode]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadStateData(stateCode)
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [stateCode]);

  return { data, loading, error };
}

// Helper to get category colors from loaded state data
function getCatColors(stateData) {
  if (!stateData) return {};
  const colors = {};
  (stateData.stateConfig.categories || []).forEach((c) => { colors[c.id] = c.color; });
  return colors;
}

// Helper to get price based on residency
function getPrice(item, isResident) {
  if (item.priceResident !== undefined) {
    return isResident ? item.priceResident : item.priceNonResident;
  }
  return item.price ?? 0;
}

// Helper to check if a stamp/permit has residency-specific pricing
function getStampPrice(stamp, isResident) {
  if (stamp.priceResident !== undefined) {
    return isResident ? stamp.priceResident : stamp.priceNonResident;
  }
  return stamp.price ?? 0;
}


// ═══════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════

function parseDate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Lighten or darken a hex color by an amount (-255 to 255)
function adjustColor(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  let r = Math.min(255, Math.max(0, (num >> 16) + amount));
  let g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  let b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateInRange(date, start, end) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= s && d <= e;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ═══════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════

const I = {
  Wallet: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="2" y="6" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <circle cx="17" cy="14" r="1.5" fill="currentColor" />
    </svg>
  ),
  Cart: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
    </svg>
  ),
  Calendar: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Book: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
  ),
  Check: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Arrow: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  ArrowLeft: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Plus: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      {...p}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Upload: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  Shield: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Link: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),
  Search: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  ),
  Info: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
  Leaf: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89-.82C8 16 11 12 17 8z" />
      <path d="M20.18 2.66C14 5 10 9 7.5 14.5" />
    </svg>
  ),
  ChevRight: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ChevLeft: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Alert: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  X: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Crosshair: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="22" y1="12" x2="18" y2="12" />
      <line x1="6" y1="12" x2="2" y2="12" />
      <line x1="12" y1="6" x2="12" y2="2" />
      <line x1="12" y1="22" x2="12" y2="18" />
    </svg>
  ),
  Minus: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      {...p}
    >
      <path d="M5 12h14" />
    </svg>
  ),
  Home: (p) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),

};
const icon = (C, s = 20) => <C aria-hidden="true" style={{ width: s, height: s }} />;

// Emoji → Game Icons mapping (all icons from react-icons/gi for consistency)
const EMOJI_ICON_MAP = {
  "🦌": GiDeer,
  "🐻": GiBearFace,
  "🦃": GiRooster,
  "🫎": GiStagHead,
  "🦆": GiDuck,
  "🪿": GiGoose,
  "🕊️": GiDove,
  "🕊": GiDove,
  "🐦": GiSparrow,
  "🐇": GiRabbit,
  "🐿️": GiSquirrel,
  "🐿": GiSquirrel,
  "🐗": GiBoar,
  "🐏": GiRam,
  "🐐": GiGoat,
  "🦊": GiFoxHead,
  "🐺": GiWolfHead,
  "🐱": GiLynxHead,
  "🦝": GiRaccoonHead,
  "🐊": GiCrocJaws,
  "🦬": GiBison,
  "🫏": GiDeerHead,
  "🦦": GiBeaver,
  "🦅": GiEagleHead,
  "🐾": GiPawPrint,
  "🐷": GiPig,
  "🎟️": GiTicket,
  "🎟": GiTicket,
};

const AnimalIcon = ({ emoji, size = 20, style: s = {} }) => {
  const Comp = EMOJI_ICON_MAP[emoji];
  if (!Comp) return <span style={{ fontSize: size, lineHeight: 1, ...s }}>{emoji}</span>;
  return <Comp aria-hidden="true" style={{ width: size, height: size, flexShrink: 0, ...s }} />;
};

// ═══════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════

const T = {
  bg: "#F5F3EE",
  surface: "#FEFDFB",
  card: "#FFFDF9",
  cardHover: "#F0EDE6",
  border: "#DDD8CE",
  borderLight: "#C9C3B8",
  accent: "#034732",
  accentDim: "#022B1F",
  accentBg: "rgba(3,71,50,0.07)",
  accentBg2: "rgba(3,71,50,0.14)",
  gold: "#D4A843",
  goldText: "#8B6914",
  goldDim: "rgba(212,168,67,0.14)",
  blue: "#1565C0",
  blueDim: "rgba(21,101,192,0.08)",
  red: "#B71C1C",
  redDim: "rgba(183,28,28,0.08)",
  orange: "#E65100",
  orangeDim: "rgba(230,81,0,0.08)",
  text: "#1A1A1A",
  textMuted: "#4A4A4A",
  textDim: "#757575",
  coBrand: "#1D428A",
};
const font = "'Figtree','Segoe UI',system-ui,sans-serif";
const fontD = "'Fraunces','Georgia',serif";

// Responsive sizing constants — UI/UX Pro Max: 16px min body, 44px min tap
const R = {
  pagePad: "clamp(18px, 5vw, 28px)",
  cardPad: "clamp(16px, 4vw, 22px)",
  btnPad: "clamp(14px, 3vw, 16px) clamp(22px, 5vw, 28px)",
  btnFontSize: "clamp(15px, 3.8vw, 17px)",
  titleSize: "clamp(24px, 6vw, 32px)",
  bodySize: "clamp(16px, 4vw, 18px)",
  subSize: "clamp(15px, 3.5vw, 17px)",
  smallSize: "clamp(13px, 3.2vw, 15px)",
  tinySize: "clamp(11px, 2.8vw, 13px)",
  labelSize: "clamp(11px, 2.6vw, 13px)",
  tap: 48, // 48px touch target — exceeds 44px WCAG minimum for comfort
  tapSmall: 44, // matches 44px WCAG minimum
  iconBtn: 48, // icon button size — exceeds 44px minimum
  maxW: 640,
};

// ═══════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════

const inputStyle = {
  width: "100%",
  padding: `${R.cardPad}`,
  fontSize: R.bodySize,
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  color: T.text,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: font,
  minHeight: R.tap,
};

const SectionTitle = ({ children, sub }) => (
  <div style={{ marginBottom: "clamp(12px, 3vw, 18px)" }}>
    <h2
      style={{
        fontFamily: fontD,
        fontSize: R.titleSize,
        fontWeight: 700,
        margin: 0,
        letterSpacing: "-0.02em",
        color: T.text,
      }}
    >
      {children}
    </h2>
    {sub && (
      <p
        style={{
          color: T.textMuted,
          fontSize: R.subSize,
          margin: "4px 0 0",
          lineHeight: 1.5,
        }}
      >
        {sub}
      </p>
    )}
  </div>
);

const Btn = ({
  children,
  onClick,
  variant = "primary",
  disabled,
  full,
  style: s = {},
}) => {
  const base = {
    padding: R.btnPad,
    borderRadius: 10,
    fontSize: R.btnFontSize,
    fontWeight: 600,
    fontFamily: font,
    cursor: disabled ? "default" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
    border: "none",
    width: full ? "100%" : "auto",
    opacity: disabled ? 0.45 : 1,
    minHeight: R.tap,
    ...s,
  };
  const v = {
    primary: { ...base, background: T.accent, color: "#fff" },
    secondary: {
      ...base,
      background: T.bg,
      color: T.textMuted,
      border: `1px solid ${T.border}`,
    },
    ghost: {
      ...base,
      background: "transparent",
      color: T.textMuted,
      padding: `clamp(8px, 2vw, 12px) clamp(12px, 3vw, 16px)`,
      minHeight: R.tapSmall,
    },
    gold: { ...base, background: T.gold, color: "#fff" },
    orange: { ...base, background: T.orange, color: "#fff" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={v[variant]}>
      {children}
    </button>
  );
};

const Card = ({ children, selected, onClick, style: s = {}, className = "" }) => (
  <div
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
    className={`${onClick ? "card-interactive" : ""} ${className}`}
    style={{
      padding: `${R.cardPad}`,
      borderRadius: 14,
      border: selected ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
      background: selected ? T.accentBg : T.card,
      boxShadow: "0 1px 4px rgba(26,26,26,0.06)",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
      ...s,
    }}
  >
    {children}
  </div>
);

const Badge = ({ children, color = T.accent, bg = T.accentBg }) => (
  <span
    style={{
      fontSize: R.tinySize,
      fontWeight: 700,
      background: bg,
      color,
      padding: "3px 10px",
      borderRadius: 5,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

// Page wrapper with entrance animation
const Page = ({ children, direction = "up", style: s = {} }) => (
  <div
    className={direction === "right" ? "page-enter-right" : direction === "left" ? "page-enter-left" : "page-enter"}
    style={{ padding: R.pagePad, ...s }}
  >
    {children}
  </div>
);

const Label = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    style={{
      display: "block",
      fontSize: R.labelSize,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: T.textDim,
      marginBottom: 10,
      marginTop: 4,
    }}
  >
    {children}
  </label>
);

const SubNav = ({ onBack, title }) => (
  <div
    className="animate-fade-in"
    role="navigation"
    aria-label={`${title} navigation`}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 0 16px",
      borderBottom: `1px solid ${T.border}`,
      marginBottom: 16,
    }}
  >
    <button
      onClick={onBack}
      aria-label="Go back"
      className="nav-btn"
      style={{
        width: R.iconBtn,
        height: R.iconBtn,
        borderRadius: 10,
        background: T.bg,
        border: `1px solid ${T.border}`,
        color: T.textMuted,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon(I.ArrowLeft, 18)}
    </button>
    <span style={{ fontSize: R.bodySize, fontWeight: 600, fontFamily: font }}>
      {title}
    </span>
  </div>
);

const ObtainBadge = ({ obtain }) => {
  if (obtain === "draw-only")
    return (
      <Badge color={T.orange} bg={T.orangeDim}>
        DRAW ONLY
      </Badge>
    );
  if (obtain === "draw-or-otc")
    return (
      <Badge color={T.blue} bg={T.blueDim}>
        DRAW / OTC
      </Badge>
    );
  if (obtain === "otc-permit")
    return (
      <Badge color="#7B1FA2" bg="rgba(123,31,162,0.08)">
        OTC + PERMIT
      </Badge>
    );
  return (
    <Badge color={T.accent} bg={T.accentBg}>
      OTC
    </Badge>
  );
};

// ═══════════════════════════════════════════
// HOME TAB
// ═══════════════════════════════════════════

const HOME_TILES = [
  {
    id: "calendar",
    num: "01",
    label: "Hunt Calendar",
    desc: "Season dates & key deadlines",
    icon: I.Calendar,
  },
  {
    id: "buy",
    num: "02",
    label: "Buy Licenses",
    desc: "We figure out what you need",
    icon: I.Cart,
  },
  {
    id: "wallet",
    num: "03",
    label: "License Wallet",
    desc: "Digital credentials, always ready",
    icon: I.Wallet,
  },
  {
    id: "regulations",
    num: "04",
    label: "Regulations",
    desc: "All CPW brochures & links",
    icon: I.Book,
  },
];

const HomeTab = ({ onNavigate }) => (
  <Page>
    {/* Hero — left-aligned */}
    <div
      style={{
        paddingTop: "clamp(20px, 5vw, 36px)",
        marginBottom: "clamp(24px, 6vw, 36px)",
      }}
    >
      <h1
        className="animate-fade-in-up"
        style={{
          fontFamily: fontD,
          fontSize: "clamp(32px, 9vw, 46px)",
          fontWeight: 700,
          letterSpacing: "-0.025em",
          margin: 0,
          color: T.text,
          lineHeight: 1.1,
        }}
      >
        Everything you{"\n"}need to hunt.
      </h1>
      <p
        className="animate-fade-in-up"
        style={{
          fontSize: R.bodySize,
          color: T.textMuted,
          margin: "clamp(10px, 2.5vw, 16px) 0 0",
          lineHeight: 1.5,
          animationDelay: "80ms",
        }}
      >
        Licenses, seasons, and regulations — organized so you can stop guessing.
      </p>
    </div>

    {/* 2x2 Tile Grid */}
    <div
      className="stagger-scale"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 0,
        borderRadius: 16,
        border: `1px solid ${T.border}`,
        background: T.card,
        boxShadow: "0 2px 12px rgba(26,26,26,0.06)",
        overflow: "hidden",
        marginBottom: "clamp(24px, 6vw, 40px)",
      }}
    >
      {HOME_TILES.map((tile, idx) => (
        <button
          key={tile.id}
          onClick={() => onNavigate(tile.id)}
          className="card-interactive"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            padding: "clamp(20px, 5vw, 28px)",
            border: "none",
            borderRight: idx % 2 === 0 ? `1px solid ${T.border}` : "none",
            borderBottom: idx < 2 ? `1px solid ${T.border}` : "none",
            background: T.card,
            cursor: "pointer",
            fontFamily: font,
            gap: "clamp(6px, 1.5vw, 10px)",
            borderRadius: 0,
          }}
        >
          <div
            style={{
              fontSize: R.smallSize,
              fontWeight: 600,
              color: T.accent,
              letterSpacing: "0.02em",
            }}
          >
            {tile.num}
          </div>
          <div
            style={{
              color: T.accent,
              marginTop: "clamp(2px, 0.5vw, 4px)",
            }}
          >
            {icon(tile.icon, "clamp(28px, 7vw, 36px)")}
          </div>
          <div
            style={{
              fontFamily: fontD,
              fontSize: "clamp(17px, 4.5vw, 21px)",
              fontWeight: 700,
              color: T.text,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
              marginTop: "clamp(2px, 0.5vw, 6px)",
            }}
          >
            {tile.label}
          </div>
          <div
            style={{
              fontSize: R.smallSize,
              color: T.textMuted,
              lineHeight: 1.4,
            }}
          >
            {tile.desc}
          </div>
          <div
            style={{
              marginTop: "clamp(6px, 1.5vw, 10px)",
              color: T.accent,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: R.smallSize,
              fontWeight: 600,
            }}
          >
            Open →
          </div>
        </button>
      ))}
    </div>
  </Page>
);

// ═══════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════

export default function App() {
  const savedTab = loadFromStorage(STORAGE_KEYS.lastTab, "home");
  const [tab, setTab] = useState(savedTab);

  // Restore persisted state on mount
  const savedActiveState = loadFromStorage(STORAGE_KEYS.activeState, null) || Object.keys(SUPPORTED_STATES)[0];
  const allSavedResident = loadFromStorage(STORAGE_KEYS.resident, {});

  // myHunts: flat array of { speciesId, seasonLabel, start, end, state }
  const [myHunts, setMyHunts] = useState(() => {
    const raw = loadFromStorage(STORAGE_KEYS.hunts, []);
    return Array.isArray(raw) ? raw : [];
  });
  const [activeState, setActiveState] = useState(savedActiveState);
  const [isResident, setIsResident] = useState(
    savedActiveState ? (allSavedResident[savedActiveState] ?? null) : null
  );

  const { data: stateData, loading: stateLoading } = useStateData(activeState);
  const scrollRef = useRef(null);
  const scrollTop = () => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Persist activeState whenever it changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.activeState, activeState);
  }, [activeState]);

  // Persist myHunts whenever they change
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.hunts, myHunts);
  }, [myHunts]);

  // Persist isResident for the current state whenever it changes
  useEffect(() => {
    if (!activeState) return;
    const all = loadFromStorage(STORAGE_KEYS.resident, {});
    all[activeState] = isResident;
    saveToStorage(STORAGE_KEYS.resident, all);
  }, [isResident, activeState]);

  // Persist last active tab
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.lastTab, tab);
  }, [tab]);

  const selectState = useCallback((code) => {
    setActiveState(code);
    const savedRes = loadFromStorage(STORAGE_KEYS.resident, {});
    setIsResident(savedRes[code] ?? null);
  }, []);

  // Add one or more hunt entries (each is { speciesId, seasonLabel, start, end, state })
  const addHunt = useCallback((entry) => {
    setMyHunts((prev) => {
      // Avoid duplicates by speciesId + seasonLabel + state
      const exists = prev.some(
        (h) => h.speciesId === entry.speciesId && h.seasonLabel === entry.seasonLabel && h.state === entry.state
      );
      if (exists) {
        // Update existing entry (e.g. dates changed)
        return prev.map((h) =>
          h.speciesId === entry.speciesId && h.seasonLabel === entry.seasonLabel && h.state === entry.state
            ? { ...h, ...entry }
            : h
        );
      }
      return [...prev, entry];
    });
  }, []);

  // Remove a specific entry (speciesId + seasonLabel), or all entries for a speciesId
  const removeHunt = useCallback((speciesId, seasonLabel) => {
    setMyHunts((prev) => {
      if (seasonLabel) {
        return prev.filter((h) => !(h.speciesId === speciesId && h.seasonLabel === seasonLabel));
      }
      return prev.filter((h) => h.speciesId !== speciesId);
    });
  }, []);

  const tabs = [
    { id: "home", label: "Home", icon: I.Home },
    { id: "calendar", label: "Calendar", icon: I.Calendar },
    { id: "buy", label: "Buy", icon: I.Cart },
    { id: "wallet", label: "Wallet", icon: I.Wallet },
    { id: "regulations", label: "Regs", icon: I.Book },
  ];

  return (
    <div
      style={{
        fontFamily: font,
        background: T.bg,
        color: T.text,
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: R.maxW,
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&display=swap"
        rel="stylesheet"
      />
      <header
        role="banner"
        style={{
          padding: `calc(clamp(12px, 3vw, 16px) + env(safe-area-inset-top, 0px)) ${R.pagePad} clamp(12px, 3vw, 16px)`,
          borderBottom: "none",
          background: T.accent,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
          boxShadow: "0 1px 6px rgba(26,26,26,0.06)",
        }}
      >
        <div
          style={{
            width: "clamp(36px, 9vw, 44px)",
            height: "clamp(36px, 9vw, 44px)",
            borderRadius: 12,
            background: `linear-gradient(135deg, ${T.accent}, ${adjustColor(T.accent, 30)})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: "0 2px 6px rgba(3,71,50,0.25)",
          }}
        >
          {icon(I.Wallet, 22)}
        </div>
        <div>
          <h1
            style={{
              fontFamily: fontD,
              fontWeight: 700,
              fontSize: "clamp(17px, 4.5vw, 22px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              margin: 0,
              color: "#fff",
            }}
          >
            Sportsman's Wallet
          </h1>
        </div>
      </header>
      <main
        ref={scrollRef}
        className="scroll-container"
        role="main"
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
      >
        {tab === "home" && (
          <HomeTab
            onNavigate={(id) => {
              setTab(id);
              scrollTop();
            }}
          />
        )}
        {tab === "wallet" && <WalletTab stateData={stateData} activeState={activeState} />}
        {tab === "buy" && (
          <BuyTab
            scrollTop={scrollTop}
            activeState={activeState}
            selectState={selectState}
            stateData={stateData}
            stateLoading={stateLoading}
            isResident={isResident}
            setIsResident={setIsResident}
          />
        )}
        {tab === "calendar" && (
          <CalendarTab
            myHunts={myHunts}
            addHunt={addHunt}
            removeHunt={removeHunt}
            scrollTop={scrollTop}
            goToBuy={() => {
              setTab("buy");
              scrollTop();
            }}
            stateData={stateData}
            activeState={activeState}
            selectState={selectState}
            stateLoading={stateLoading}
          />
        )}
        {tab === "regulations" && (
          <RegulationsTab
            stateData={stateData}
            activeState={activeState}
            selectState={selectState}
            stateLoading={stateLoading}
          />
        )}
      </main>
      {tab !== "home" && <nav
        role="navigation"
        aria-label="Main navigation"
        style={{
          display: "flex",
          borderTop: `1px solid ${T.border}`,
          background: T.surface,
          flexShrink: 0,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            aria-label={t.label}
            aria-current={tab === t.id ? "page" : undefined}
            onClick={() => {
              setTab(t.id);
              scrollTop();
            }}
            style={{
              flex: 1,
              padding: "12px 0 14px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              border: "none",
              background: tab === t.id ? T.accentBg : "transparent",
              color: tab === t.id ? T.accent : T.textDim,
              cursor: "pointer",
              position: "relative",
              minHeight: R.tap,
              transition: "color 0.2s ease, background-color 0.2s ease",
            }}
          >
            <div style={{ transition: "transform 0.2s ease", transform: tab === t.id ? "scale(1.15)" : "scale(1)" }}>
              {icon(t.icon, 26)}
            </div>
            <span style={{ fontSize: R.labelSize, fontWeight: tab === t.id ? 700 : 600, transition: "color 0.2s ease" }}>
              {t.label}
            </span>
            {t.id === "calendar" && myHunts.length > 0 && (
              <span
                className="tab-badge"
                aria-label={`${myHunts.length} hunts planned`}
                style={{
                  position: "absolute",
                  top: 4,
                  right: "50%",
                  marginRight: -20,
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  background: T.gold,
                  color: T.goldText,
                  fontSize: R.tinySize,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              >
                {myHunts.length}
              </span>
            )}
            {tab === t.id && (
              <div
                className="tab-indicator"
                style={{
                  width: 24,
                  height: 3,
                  borderRadius: 2,
                  background: T.accent,
                }}
              />
            )}
          </button>
        ))}
      </nav>}
    </div>
  );
}

// ═══════════════════════════════════════════
// TAB 1: WALLET
// ═══════════════════════════════════════════

function WalletTab({ stateData, activeState }) {
  const STATES = Object.entries(SUPPORTED_STATES).map(([id, s]) => ({ id, ...s }));
  const STATE_COLORS = {};
  Object.entries(SUPPORTED_STATES).forEach(([id, s]) => { STATE_COLORS[id] = s.color; });

  const [walletItems] = useState([
    {
      id: "w1",
      name: "Small Game License",
      state: "CO",
      number: "SG-2026-449821",
      valid: true,
      season: "2025-2026",
    },
    {
      id: "w2",
      name: "Habitat Stamp",
      state: "CO",
      number: "HS-2026-112034",
      valid: true,
      season: "2025-2026",
    },
    {
      id: "w3",
      name: "Gun Deer License",
      state: "WI",
      number: "WI-GD-2026-883201",
      valid: true,
      season: "2025-2026",
    },
    {
      id: "w4",
      name: "Conservation Patron License",
      state: "WI",
      number: "WI-CP-2026-117540",
      valid: true,
      season: "2025-2026",
    },
  ]);
  const [safetyCards] = useState([
    {
      id: "sc1",
      state: "CO",
      name: "Hunter Safety Card",
      certNumber: "CO-HS-2019-003421",
      issueDate: "Jun 15, 2019",
      holder: "John Doe",
    },
  ]);
  const [expanded, setExpanded] = useState(null);
  const [subPage, setSubPage] = useState(null); // null | "add-license" | "safety-card"

  const stateGroups = useMemo(() => {
    const groups = {};
    walletItems.forEach((item) => {
      if (!groups[item.state]) groups[item.state] = [];
      groups[item.state].push(item);
    });
    return groups;
  }, [walletItems]);

  // Add License sub-page
  if (subPage === "add-license") {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SubNav onBack={() => setSubPage(null)} title="Add License" />
        <SectionTitle sub="Enter your license details manually or import from a state portal.">
          Add a License
        </SectionTitle>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <Label>State</Label>
            <select style={{ ...inputStyle, appearance: "auto" }}>
              <option value="">Select state...</option>
              {STATES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>License Type</Label>
            <select style={{ ...inputStyle, appearance: "auto" }}>
              <option value="">Select type...</option>
              <option value="small-game">Small Game License</option>
              <option value="sportsman">Sportsman's License</option>
              <option value="big-game">Big Game License</option>
              <option value="fishing">Fishing License</option>
              <option value="turkey">Turkey License</option>
              <option value="waterfowl">Waterfowl Stamp</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label>License Number</Label>
            <input placeholder="e.g. SG-2026-449821" style={inputStyle} />
          </div>
          <div>
            <Label>Season Year</Label>
            <input placeholder="e.g. 2025-2026" style={inputStyle} />
          </div>
        </div>
        <Btn full disabled>
          {icon(I.Plus, 16)} Add to Wallet
        </Btn>
        <div style={{ marginTop: 20 }}>
          <Label>Or Import from State Portal</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {STATES.map((s) => (
              <Card
                key={s.id}
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 11,
                    background: `${s.color}15`,
                    border: `2px solid ${s.color}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: R.subSize,
                    fontWeight: 800,
                    color: s.color,
                    fontFamily: font,
                  }}
                >
                  {s.id}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: R.subSize, fontWeight: 600 }}>
                    Import from {s.name}
                  </div>
                  <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                    {s.id === "CO"
                      ? "Connect your CPW CID"
                      : s.id === "WI"
                      ? "Connect your Go Wild account"
                      : "Connect account"}
                  </div>
                </div>
                <div style={{ color: T.textDim }}>{icon(I.ChevRight, 16)}</div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Hunter Safety Card sub-page
  if (subPage === "safety-card") {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SubNav onBack={() => setSubPage(null)} title="Hunter Safety Card" />
        <SectionTitle sub="Your hunter education certifications.">
          Hunter Safety Cards
        </SectionTitle>

        {safetyCards.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 20,
            }}
          >
            {safetyCards.map((card) => (
              <div
                key={card.id}
                style={{
                  padding: "18px",
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${T.card}, ${T.cardHover})`,
                  border: `1px solid ${T.border}`,
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 4,
                    height: "100%",
                    background: STATE_COLORS[card.state] || T.accent,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: R.bodySize, fontWeight: 700 }}>
                      {card.name}
                    </div>
                    <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                      {card.state} • Issued {card.issueDate}
                    </div>
                  </div>
                  <Badge color={T.accent}>VALID</Badge>
                </div>
                <div
                  style={{ display: "flex", gap: 16, fontSize: R.smallSize }}
                >
                  <div>
                    <div
                      style={{
                        color: T.textDim,
                        fontSize: R.labelSize,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 2,
                      }}
                    >
                      Holder
                    </div>
                    <div style={{ fontWeight: 600 }}>{card.holder}</div>
                  </div>
                  <div>
                    <div
                      style={{
                        color: T.textDim,
                        fontSize: R.labelSize,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 2,
                      }}
                    >
                      Cert #
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: R.smallSize,
                        fontWeight: 600,
                      }}
                    >
                      {card.certNumber}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: T.accentBg,
                    fontSize: R.smallSize,
                    color: T.accent,
                    textAlign: "center",
                    fontWeight: 600,
                  }}
                >
                  Quick Display Mode — Show to Game Warden
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card
            style={{
              textAlign: "center",
              padding: "24px 16px",
              marginBottom: 20,
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>
              {icon(I.Shield, 28)}
            </div>
            <div
              style={{ fontSize: R.bodySize, fontWeight: 600, marginBottom: 4 }}
            >
              No Safety Cards Yet
            </div>
            <div style={{ fontSize: R.smallSize, color: T.textMuted }}>
              Add your hunter education certification below.
            </div>
          </Card>
        )}

        <Label>Add a Safety Card</Label>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <Label>State Issued</Label>
            <select style={{ ...inputStyle, appearance: "auto" }}>
              <option value="">Select state...</option>
              {STATES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Full Name</Label>
            <input placeholder="As shown on card" style={inputStyle} />
          </div>
          <div>
            <Label>Certificate Number</Label>
            <input placeholder="e.g. CO-HS-2019-003421" style={inputStyle} />
          </div>
        </div>
        <Btn full disabled>
          {icon(I.Plus, 16)} Add Safety Card
        </Btn>
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            borderRadius: 10,
            background: T.blueDim,
            fontSize: R.smallSize,
            color: T.blue,
            textAlign: "center",
          }}
        >
          Most states accept digital proof of hunter education. Check your
          state's regulations.
        </div>
      </div>
    );
  }

  // Main wallet view
  return (
    <div className="page-enter" style={{ padding: R.pagePad }}>
      <SectionTitle sub="Your digital credentials — all in one place.">
        Sportsman's Wallet
      </SectionTitle>
      <div className="animate-fade-in-up" style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <Btn
          variant="secondary"
          full
          style={{ flex: 1, fontSize: "clamp(16px, 4vw, 18px)", padding: "clamp(16px, 3.5vw, 18px) clamp(22px, 5vw, 30px)" }}
          onClick={() => setSubPage("add-license")}
        >
          {icon(I.Plus, 18)} Add License
        </Btn>
        <Btn
          variant="secondary"
          full
          style={{ flex: 1, fontSize: "clamp(16px, 4vw, 18px)", padding: "clamp(16px, 3.5vw, 18px) clamp(22px, 5vw, 30px)" }}
          onClick={() => setSubPage("safety-card")}
        >
          {icon(I.Shield, 18)} Safety Card
        </Btn>
      </div>

      {Object.entries(stateGroups).map(([stateId, items]) => {
        const stateInfo = STATES.find((s) => s.id === stateId);
        const stateColor = stateInfo?.color || T.coBrand;
        return (
          <div key={stateId} style={{ marginBottom: 20 }}>
            <Label>{stateInfo?.name || stateId}</Label>
            <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((item) => (
                <div
                  key={item.id}
                  className="license-card"
                  onClick={() =>
                    setExpanded(expanded === item.id ? null : item.id)
                  }
                  style={{
                    padding: "16px 18px",
                    borderRadius: 14,
                    cursor: "pointer",
                    background: `linear-gradient(135deg, ${T.card}, ${T.cardHover})`,
                    border: `1px solid ${T.border}`,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 4,
                      height: "100%",
                      background: stateColor,
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: R.bodySize, fontWeight: 700 }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                        {item.state} • {item.season}
                      </div>
                    </div>
                    <Badge color={T.accent}>VALID</Badge>
                  </div>
                  {expanded === item.id && (
                    <div
                      className="license-expand"
                      style={{
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: `1px solid ${T.border}`,
                        fontSize: R.subSize,
                        color: T.textMuted,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>License #</span>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: R.smallSize,
                            color: T.text,
                          }}
                        >
                          {item.number}
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: 10,
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: T.accentBg,
                          fontSize: R.smallSize,
                          color: T.accent,
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        Quick Display Mode — Show to Game Warden
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════
// TAB 2: BUY LICENSES — MULTI-STATE
// ═══════════════════════════════════════════

function BuyTab({ scrollTop, activeState, selectState, stateData, stateLoading, isResident, setIsResident }) {
  const [step, setStep] = useState("pick-state");
  const [hasCID, setHasCID] = useState(null);
  const [cidInput, setCidInput] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);

  // Reset step when state changes
  useEffect(() => {
    if (activeState && stateData && step === "pick-state") {
      // If a state is loaded and we're at pick-state, advance
    }
  }, [activeState, stateData, step]);

  const go = (s) => {
    setStep(s);
    scrollTop();
  };
  const reset = () => {
    selectState(null);
    setStep("pick-state");
    setHasCID(null);
    setCidInput("");
    setSelectedCats([]);
    setSelectedSpecies([]);
  };

  const toggleCat = (id) =>
    setSelectedCats((p) =>
      p.includes(id) ? p.filter((c) => c !== id) : [...p, id]
    );
  const toggleSpecies = (id) =>
    setSelectedSpecies((p) =>
      p.includes(id) ? p.filter((s) => s !== id) : [...p, id]
    );

  const cfg = stateData?.stateConfig;
  const SPECIES = stateData?.species || [];
  const CATEGORIES = cfg?.categories || [];
  const stateColor = cfg?.stateColor || SUPPORTED_STATES[activeState]?.color || T.accent;
  const stateName = cfg?.stateName || SUPPORTED_STATES[activeState]?.name || activeState;

  // Step 1: State Selection
  if (step === "pick-state") {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SectionTitle sub="Choose a state to start.">Buy Licenses</SectionTitle>
        <div
          className="stagger-scale"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 8,
          }}
        >
          {ALL_STATES_LIST.map((s) => {
            const supported = !!SUPPORTED_STATES[s];
            return (
              <button
                key={s}
                onClick={supported ? () => {
                  selectState(s);
                  go("residency");
                } : undefined}
                style={{
                  padding: "14px 0",
                  borderRadius: 10,
                  fontSize: R.bodySize,
                  fontWeight: 700,
                  fontFamily: font,
                  cursor: supported ? "pointer" : "default",
                  border: supported
                    ? `2px solid ${SUPPORTED_STATES[s].color}`
                    : `1px solid ${T.border}`,
                  background: supported ? `${SUPPORTED_STATES[s].color}14` : "transparent",
                  color: supported ? SUPPORTED_STATES[s].color : T.textDim,
                  opacity: supported ? 1 : 0.35,
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            borderRadius: 10,
            background: T.blueDim,
            fontSize: R.smallSize,
            color: T.blue,
            textAlign: "center",
          }}
        >
          {Object.keys(SUPPORTED_STATES).length} states available: {Object.values(SUPPORTED_STATES).map(s => s.name).join(", ")}. More coming soon.
        </div>
      </div>
    );
  }

  // Loading state
  if (stateLoading || !stateData) {
    return (
      <div className="page-enter" style={{ padding: R.pagePad, textAlign: "center" }}>
        <SubNav onBack={() => { reset(); }} title="Loading..." />
        <div style={{ padding: "40px 0", color: T.textDim, fontSize: R.bodySize }}>
          Loading {stateName} data...
        </div>
      </div>
    );
  }

  // Step 2: Residency Question
  if (step === "residency") {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SubNav onBack={() => { reset(); }} title={stateName} />
        <div
          className="animate-spring-in"
          style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${stateColor}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 800, color: stateColor,
            fontFamily: font, marginBottom: 16, border: `2px solid ${stateColor}`,
          }}
        >
          {activeState}
        </div>
        <SectionTitle sub={`This affects license prices and available options.`}>
          Are you a resident of {stateName}?
        </SectionTitle>
        <div className="animate-fade-in-up" style={{ display: "flex", gap: 10 }}>
          <Btn full onClick={() => { setIsResident(true); go("cid-check"); }} style={{ flex: 1 }}>
            Yes, I'm a resident
          </Btn>
          <Btn full variant="secondary" onClick={() => { setIsResident(false); go("cid-check"); }} style={{ flex: 1 }}>
            No, non-resident
          </Btn>
        </div>
        <div className="animate-fade-in" style={{
          marginTop: 16, padding: "12px 16px", borderRadius: 10,
          background: T.blueDim, fontSize: R.smallSize, color: T.blue, textAlign: "center", lineHeight: 1.5,
          animationDelay: "0.15s",
        }}>
          Residency determines your license prices. Non-resident licenses are typically more expensive.
        </div>
      </div>
    );
  }

  // Step 3: CID / Account Check (state-specific)
  if (step === "cid-check") {
    const portalName = cfg.licensingPortalName || "State Portal";
    const portalUrl = cfg.licensingPortalUrl || "#";
    const accountName = cfg.accountIdName || "Account ID";
    const accountFull = cfg.accountIdFull || `${stateName} Account Number`;

    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SubNav onBack={() => go("residency")} title={stateName} />
        <div
          className="animate-spring-in"
          style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${stateColor}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 800, color: stateColor,
            fontFamily: font, marginBottom: 16, border: `2px solid ${stateColor}`,
          }}
        >
          {activeState}
        </div>
        <SectionTitle sub={accountFull}>
          Do you have a {stateName} {accountName}?
        </SectionTitle>
        {hasCID === null && (
          <div className="animate-fade-in-up" style={{ display: "flex", gap: 10 }}>
            <Btn full onClick={() => setHasCID(true)} style={{ flex: 1 }}>
              Yes, I have one
            </Btn>
            <Btn full variant="secondary" onClick={() => setHasCID(false)} style={{ flex: 1 }}>
              No, I need one
            </Btn>
          </div>
        )}
        {hasCID === true && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            <input
              placeholder={`Enter your ${accountName}`}
              value={cidInput}
              onChange={(e) => setCidInput(e.target.value)}
              style={inputStyle}
            />
            <Btn full onClick={() => go("what-to-hunt")} disabled={!cidInput}>
              Login & Continue {icon(I.Arrow, 16)}
            </Btn>
          </div>
        )}
        {hasCID === false && (
          <div style={{ marginTop: 8 }}>
            <Card style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ color: T.blue }}>{icon(I.Info, 18)}</div>
                <div>
                  <div style={{ fontSize: R.subSize, fontWeight: 600, marginBottom: 4 }}>
                    Create a {portalName} Account
                  </div>
                  <div style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.5 }}>
                    Create an account at {portalName} to get your {accountName}.
                  </div>
                </div>
              </div>
            </Card>
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              <Btn full variant="gold">
                Go to {portalName} {icon(I.Link, 14)}
              </Btn>
            </a>
            <div style={{ marginTop: 12 }}>
              <Btn full variant="ghost" onClick={() => setHasCID(true)}>
                I have my {accountName} now
              </Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Step 4: What are you hunting? (combined categories + species)
  if (step === "what-to-hunt") {
    const activeCat = selectedCats.length > 0 ? selectedCats : CATEGORIES.map(c => c.id);
    const filteredSpecies = SPECIES.filter((s) => activeCat.includes(s.cat));

    return (
      <div className="page-enter" style={{ padding: R.pagePad, paddingBottom: 80 }}>
        <SubNav onBack={() => go("cid-check")} title={stateName} />
        <SectionTitle sub="Select everything you plan to hunt — we'll figure out the rest.">
          What are you hunting?
        </SectionTitle>

        {/* Category filter pills */}
        <div className="scroll-horizontal" style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
          <button
            className="filter-pill"
            onClick={() => setSelectedCats([])}
            style={{
              padding: "clamp(8px, 2vw, 12px) clamp(12px, 3vw, 18px)",
              borderRadius: 8, fontSize: R.smallSize, fontWeight: 700, fontFamily: font, whiteSpace: "nowrap",
              border: selectedCats.length === 0 ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
              background: selectedCats.length === 0 ? T.accentBg2 : T.card,
              color: selectedCats.length === 0 ? T.accent : T.textDim,
              cursor: "pointer", minHeight: R.tapSmall,
            }}
          >All</button>
          {CATEGORIES.map((cat) => {
            const sel = selectedCats.includes(cat.id);
            return (
              <button
                key={cat.id} className="filter-pill"
                onClick={() => toggleCat(cat.id)}
                style={{
                  padding: "clamp(8px, 2vw, 12px) clamp(12px, 3vw, 18px)",
                  borderRadius: 8, fontSize: R.smallSize, fontWeight: 700, fontFamily: font, whiteSpace: "nowrap",
                  border: sel ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
                  background: sel ? T.accentBg2 : T.card,
                  color: sel ? T.accent : T.textDim,
                  cursor: "pointer", minHeight: R.tapSmall,
                }}
              >{cat.icon ? <><AnimalIcon emoji={cat.icon} size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /></> : ""}{cat.label}</button>
            );
          })}
        </div>

        {/* Species list */}
        <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {filteredSpecies.map((sp) => {
            const sel = selectedSpecies.includes(sp.id);
            return (
              <Card key={sp.id} selected={sel} onClick={() => toggleSpecies(sp.id)}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    border: sel ? `2px solid ${T.accent}` : `2px solid ${T.borderLight}`,
                    background: sel ? T.accent : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", flexShrink: 0,
                    transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                  }}>
                    {sel && icon(I.Check, 15)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: R.bodySize, fontWeight: 600 }}>{sp.name}</span>
                      <ObtainBadge obtain={sp.obtain} />
                    </div>
                    <div style={{ fontSize: R.smallSize, color: T.textDim, marginTop: 2 }}>{sp.season}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Btn full onClick={() => go("recommendations")} disabled={selectedSpecies.length === 0}>
          See What I Need ({selectedSpecies.length} selected) {icon(I.Arrow, 16)}
        </Btn>
      </div>
    );
  }

  // Step 6: Recommendations
  if (step === "recommendations") {
    return (
      <Recommendations
        selectedSpecies={selectedSpecies}
        onReset={reset}
        scrollTop={scrollTop}
        stateData={stateData}
        isResident={isResident}
        activeState={activeState}
      />
    );
  }

  return null;
}

// ═══════════════════════════════════════════
// SMART RECOMMENDATION ENGINE — MULTI-STATE
// ═══════════════════════════════════════════

function Recommendations({ selectedSpecies, onReset, scrollTop, stateData, isResident, activeState }) {
  const cfg = stateData.stateConfig;
  const SPECIES = stateData.species;
  const STAMPS_PERMITS = cfg.stampsPermits || {};
  const BASE_LICENSES = cfg.baseLicenses || {};
  const stateName = cfg.stateName;
  const stateColor = cfg.stateColor || SUPPORTED_STATES[activeState]?.color || T.accent;
  const portalName = cfg.licensingPortalName || "State Portal";
  const resident = isResident !== false;

  const selected = selectedSpecies
    .map((id) => SPECIES.find((s) => s.id === id))
    .filter(Boolean);

  const buyNow = selected.filter((s) => !s.obtain.includes("draw-only"));
  const drawRequired = selected.filter(
    (s) => s.obtain === "draw-only" || (s.obtain === "draw-or-otc" && s.drawInfo)
  );

  // Collect stamps/permits needed (deduplicated)
  const stampMap = {};
  [...buyNow, ...drawRequired].forEach((sp) => {
    (sp.reqs || []).forEach((reqId) => {
      const stamp = STAMPS_PERMITS[reqId];
      if (stamp && !stampMap[reqId]) stampMap[reqId] = { ...stamp, forSpecies: [] };
      if (stamp && !stampMap[reqId].forSpecies.includes(sp.name))
        stampMap[reqId].forSpecies.push(sp.name);
    });
  });
  const stamps = Object.values(stampMap);

  // Collect base licenses needed (deduplicated)
  const baseLicenseMap = {};
  selected.forEach((sp) => {
    const blId = sp.baseLicense;
    if (blId && BASE_LICENSES[blId]) {
      if (!baseLicenseMap[blId]) baseLicenseMap[blId] = { ...BASE_LICENSES[blId], forSpecies: [] };
      baseLicenseMap[blId].forSpecies.push(sp.name);
    }
  });
  // If species has no baseLicense, it typically needs the default base license
  const needsDefault = selected.some((s) => !s.baseLicense);
  const defaultBase = Object.values(BASE_LICENSES).find(
    (l) => l.id === "small-game" || l.id === "all-game" || l.id === "hunting" || l.id === "resident-hunting" || l.id === "nonresident-hunting"
  );
  if (needsDefault && defaultBase) {
    const blId = defaultBase.id;
    if (!baseLicenseMap[blId]) baseLicenseMap[blId] = { ...defaultBase, forSpecies: [] };
    baseLicenseMap[blId].forSpecies.push("General hunting");
  }
  // Draw species need qualifying license
  const needsQualifying = drawRequired.length > 0;
  if (needsQualifying && defaultBase && !baseLicenseMap[defaultBase.id]) {
    baseLicenseMap[defaultBase.id] = { ...defaultBase, forSpecies: ["Draw qualification"] };
  }

  const baseLicenses = Object.values(baseLicenseMap);

  // Calculate totals with residency pricing
  const stampTotal = stamps.reduce((a, s) => a + (getStampPrice(s, resident) || 0), 0);
  const baseTotal = baseLicenses.reduce((a, l) => {
    const p = getPrice(l, resident);
    return a + (p || 0);
  }, 0);
  const buyTotal = baseTotal + stampTotal;

  // Combo/bundle detection
  const comboTip = useMemo(() => {
    const combos = Object.values(BASE_LICENSES).filter((l) => l.badge === "BEST VALUE");
    for (const combo of combos) {
      const comboPrice = getPrice(combo, resident);
      if (comboPrice && comboPrice < buyTotal * 1.3 && buyTotal > 0) {
        const savings = buyTotal - comboPrice;
        if (savings > 0) {
          return { combo, comboPrice, savings, itemTotal: buyTotal };
        }
      }
    }
    // Check if sportsman or super combo would be cheaper
    for (const [id, lic] of Object.entries(BASE_LICENSES)) {
      if (lic.badge && !combos.includes(lic)) {
        const cp = getPrice(lic, resident);
        if (cp && cp < buyTotal) {
          return { combo: lic, comboPrice: cp, savings: buyTotal - cp, itemTotal: buyTotal };
        }
      }
    }
    return null;
  }, [baseLicenses, stamps, resident, buyTotal]);

  // Trip license detection for non-residents
  const tripLicenses = useMemo(() => {
    if (resident) return [];
    return Object.values(BASE_LICENSES).filter(
      (l) => l.name && (l.name.includes("Day") || l.name.includes("Trip") || l.name.includes("day"))
    );
  }, [resident]);

  // Draw info grouped by brochure
  const uniqueDraws = [];
  const seenBrochures = new Set();
  drawRequired.forEach((sp) => {
    if (sp.drawInfo) {
      const key = sp.drawInfo.brochureLabel || sp.drawInfo.primary || sp.id;
      if (!seenBrochures.has(key)) {
        seenBrochures.add(key);
        uniqueDraws.push({
          ...sp.drawInfo,
          species: drawRequired
            .filter((d) => {
              const dk = d.drawInfo?.brochureLabel || d.drawInfo?.primary || d.id;
              return dk === key;
            })
            .map((d) => d.name),
        });
      }
    }
  });

  // Compute deadline status
  const getDeadlineStatus = (deadlineStr) => {
    if (!deadlineStr) return null;
    const today = new Date();
    // Try to parse deadline — handle various formats
    const match = deadlineStr.match(/([A-Z][a-z]+)\s+(\d+),?\s*(\d{4})/);
    if (match) {
      const d = new Date(`${match[1]} ${match[2]}, ${match[3]}`);
      if (!isNaN(d.getTime())) {
        const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
        if (diff > 0) return { type: "open", days: diff, text: `${diff} days left to apply` };
        if (diff === 0) return { type: "today", days: 0, text: "Deadline is TODAY!" };
        return { type: "closed", days: Math.abs(diff), text: "Applications closed" };
      }
    }
    return null;
  };

  // Collect "Before You Go" notes
  const beforeYouGo = useMemo(() => {
    const notes = [];
    selected.forEach((sp) => {
      if (sp.notes) notes.push({ species: sp.name, note: sp.notes });
    });
    if (cfg.sourceNotes) notes.push({ species: "General", note: cfg.sourceNotes });
    return notes;
  }, [selected, cfg.sourceNotes]);

  return (
    <div className="page-enter" style={{ padding: R.pagePad, paddingBottom: 80 }}>
      <SubNav
        onBack={onReset}
        title="Your Requirements"
      />

      {/* Residency badge */}
      <div className="animate-fade-in" style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
        padding: "8px 12px", borderRadius: 8,
        background: `${stateColor}10`, border: `1px solid ${stateColor}25`,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7,
          background: stateColor, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: R.tinySize, fontWeight: 800,
        }}>{activeState}</div>
        <span style={{ fontSize: R.subSize, fontWeight: 600, color: stateColor }}>
          {stateName} • {resident ? "Resident" : "Non-Resident"} Pricing
        </span>
      </div>

      {/* SECTION 1: Buy Now */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: T.accent, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: R.bodySize, fontWeight: 800,
          }}>1</div>
          <div>
            <div style={{ fontSize: "clamp(16px, 4.5vw, 20px)", fontWeight: 700, fontFamily: fontD }}>Buy Now</div>
            <div style={{ fontSize: R.smallSize, color: T.textDim }}>Purchase through {portalName}</div>
          </div>
        </div>

        {/* Combo/Bundle tip */}
        {comboTip && (
          <Card style={{ marginBottom: 10, background: T.goldDim, border: `1px solid ${T.gold}30` }}>
            <div style={{ fontSize: R.smallSize, fontWeight: 700, color: T.goldText, marginBottom: 2 }}>
              {comboTip.combo.name} saves you money
            </div>
            <div style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.5 }}>
              Buying items separately: ${comboTip.itemTotal.toFixed(2)}. {comboTip.combo.name} (${comboTip.comboPrice.toFixed(2)}) saves ${comboTip.savings.toFixed(2)} and {comboTip.combo.covers.toLowerCase()}.
            </div>
          </Card>
        )}

        {/* Trip license suggestion for non-residents */}
        {tripLicenses.length > 0 && (
          <Card style={{ marginBottom: 10, background: T.blueDim, border: `1px solid ${T.blue}25` }}>
            <div style={{ fontSize: R.smallSize, fontWeight: 700, color: T.blue, marginBottom: 2 }}>
              Short trip? Consider a trip license
            </div>
            {tripLicenses.map((tl) => (
              <div key={tl.id} style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.5 }}>
                {tl.name}: ${(getPrice(tl, false) || 0).toFixed(2)} — {tl.covers}
              </div>
            ))}
          </Card>
        )}

        {/* Grouped by purpose — what species each item covers */}
        {baseLicenses.length > 0 && (
          <>
            <Label>Base Licenses</Label>
            {baseLicenses.map((lic) => {
              const price = getPrice(lic, resident);
              return (
                <div key={lic.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", borderBottom: `1px solid ${T.border}`,
                }}>
                  <div>
                    <div style={{ fontSize: R.subSize, fontWeight: 600 }}>{lic.name}</div>
                    <div style={{ fontSize: R.smallSize, color: T.textDim }}>{lic.covers}</div>
                    {lic.forSpecies && lic.forSpecies.length > 0 && (
                      <div style={{ fontSize: R.smallSize, color: T.accent, marginTop: 2 }}>
                        For: {lic.forSpecies.join(", ")}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: R.bodySize, fontWeight: 700, fontFamily: fontD }}>
                    {price === null ? "N/A" : price === 0 ? "FREE" : `$${price.toFixed(2)}`}
                  </span>
                </div>
              );
            })}
          </>
        )}

        {stamps.length > 0 && (
          <>
            <Label>Required Stamps & Permits</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {stamps.map((s) => {
                const price = getStampPrice(s, resident);
                return (
                  <Card key={s.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: s.type === "federal" ? T.goldDim : T.accentBg,
                        color: s.type === "federal" ? T.goldText : T.accent,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>{icon(I.Check, 14)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: R.subSize, fontWeight: 600 }}>{s.name}</div>
                        <div style={{ fontSize: R.smallSize, color: T.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          For: {s.forSpecies.join(", ")}
                        </div>
                      </div>
                      <span style={{
                        fontSize: R.subSize, fontWeight: 700, fontFamily: fontD,
                        color: price === 0 ? T.accent : T.text,
                      }}>
                        {price === null ? "N/A" : price === 0 ? "FREE" : `$${price.toFixed(2)}`}
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        <div style={{
          display: "flex", justifyContent: "space-between",
          padding: "14px 0", borderTop: `2px solid ${T.border}`, marginTop: 12,
        }}>
          <span style={{ fontSize: R.bodySize, fontWeight: 700, fontFamily: fontD }}>Purchasable Total</span>
          <span style={{ fontSize: 24, fontWeight: 700, fontFamily: fontD, color: T.accent }}>
            ${buyTotal.toFixed(2)}
          </span>
        </div>
      </div>

      {/* SECTION 2: Apply for Draw */}
      {drawRequired.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.orange, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: R.bodySize, fontWeight: 800,
            }}>2</div>
            <div>
              <div style={{ fontSize: "clamp(16px, 4.5vw, 20px)", fontWeight: 700, fontFamily: fontD }}>Apply for Draw</div>
              <div style={{ fontSize: R.smallSize, color: T.textDim }}>Separate application on {portalName}</div>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {drawRequired.map((sp) => (
              <span key={sp.id} style={{
                padding: "6px 12px", borderRadius: 7,
                background: T.orangeDim, color: T.orange,
                fontSize: R.smallSize, fontWeight: 600,
              }}>
                <AnimalIcon emoji={sp.icon} size={14} style={{ verticalAlign: "middle" }} /> {sp.name}
              </span>
            ))}
          </div>

          {uniqueDraws.map((d, i) => {
            const deadline = getDeadlineStatus(d.primary);
            return (
              <div key={i} style={{
                marginBottom: 12, borderRadius: 12, overflow: "hidden",
                border: `2px solid ${T.orange}`, background: T.orangeDim,
              }}>
                <div style={{
                  padding: "10px 14px", background: `${T.orange}18`,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <div style={{ color: T.orange }}>{icon(I.Alert, 18)}</div>
                  <span style={{ fontSize: R.subSize, fontWeight: 700, color: T.orange }}>
                    Draw Required — {d.species.join(", ")}
                  </span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    {d.primary && (
                      <div style={{
                        flex: 1, padding: "10px 12px", borderRadius: 10,
                        background: T.surface, border: `1px solid ${T.border}`,
                      }}>
                        <div style={{
                          fontSize: R.tinySize, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.06em", color: T.orange, marginBottom: 2,
                        }}>Primary Deadline</div>
                        <div style={{ fontSize: R.smallSize, fontWeight: 700 }}>{d.primary}</div>
                        {deadline && (
                          <div style={{
                            fontSize: R.tinySize, fontWeight: 700, marginTop: 4,
                            color: deadline.type === "open" ? T.accent : deadline.type === "today" ? T.red : T.textDim,
                          }}>
                            {deadline.type === "open" && `⏰ ${deadline.text}`}
                            {deadline.type === "today" && `🔴 ${deadline.text}`}
                            {deadline.type === "closed" && `✓ ${deadline.text}`}
                          </div>
                        )}
                      </div>
                    )}
                    {d.secondary && (
                      <div style={{
                        flex: 1, padding: "10px 12px", borderRadius: 10,
                        background: T.surface, border: `1px solid ${T.border}`,
                      }}>
                        <div style={{
                          fontSize: R.tinySize, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.06em", color: T.textDim, marginBottom: 2,
                        }}>Secondary</div>
                        <div style={{ fontSize: R.smallSize, fontWeight: 600 }}>{d.secondary}</div>
                      </div>
                    )}
                  </div>
                  <a href={d.shopUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block", marginBottom: 6 }}>
                    <div style={{
                      padding: "12px 16px", borderRadius: 10,
                      background: T.orange, color: "#fff",
                      fontSize: R.subSize, fontWeight: 700, textAlign: "center",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}>
                      Apply on {portalName} {icon(I.Arrow, 14)}
                    </div>
                  </a>
                  {d.brochure && (
                    <a href={d.brochure} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: R.smallSize, fontWeight: 600, color: T.blue,
                      textDecoration: "none", display: "flex", alignItems: "center",
                      justifyContent: "center", gap: 4, padding: "4px 0",
                    }}>
                      {icon(I.Book, 13)} {d.brochureLabel}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SECTION 3: Before You Go */}
      {beforeYouGo.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.blue, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: R.bodySize, fontWeight: 800,
            }}>3</div>
            <div>
              <div style={{ fontSize: "clamp(16px, 4.5vw, 20px)", fontWeight: 700, fontFamily: fontD }}>Before You Go</div>
              <div style={{ fontSize: R.smallSize, color: T.textDim }}>Requirements & restrictions</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {beforeYouGo.map((item, i) => (
              <Card key={i}>
                <div style={{ fontSize: R.subSize, fontWeight: 600, marginBottom: 2 }}>{item.species}</div>
                <div style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.6 }}>{item.note}</div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Label>Your {selected.length} selected species</Label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
        {selected.map((sp) => (
          <div key={sp.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 0", borderBottom: `1px solid ${T.border}`,
          }}>
            <AnimalIcon emoji={sp.icon} size={18} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: R.subSize, fontWeight: 600 }}>{sp.name}</span>
            </div>
            <ObtainBadge obtain={sp.obtain} />
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Btn full>Complete Purchase — ${buyTotal.toFixed(2)}</Btn>
        <Btn full variant="secondary" onClick={onReset}>Start Over</Btn>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// SPECIES DETAIL — INTERACTIVE SEASON ACCORDION
// ═══════════════════════════════════════════

function SeasonMiniCalendar({ seasonStart, seasonEnd, selectedStart, selectedEnd, onSelect }) {
  // Compact inline calendar constrained to the season window
  const sDate = parseDate(seasonStart);
  const eDate = parseDate(seasonEnd);

  // Determine which months to show (start month through end month)
  const months = [];
  let cur = new Date(sDate.getFullYear(), sDate.getMonth(), 1);
  const last = new Date(eDate.getFullYear(), eDate.getMonth(), 1);
  while (cur <= last) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }

  const [visibleIdx, setVisibleIdx] = useState(0);
  const { year, month } = months[visibleIdx] || months[0];
  const dim = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const handleDayClick = (dateStr) => {
    if (!selectedStart || (selectedStart && selectedEnd)) {
      onSelect(dateStr, null);
    } else {
      if (dateStr < selectedStart) {
        onSelect(dateStr, selectedStart);
      } else {
        onSelect(selectedStart, dateStr);
      }
    }
  };

  return (
    <div style={{ paddingTop: 8, paddingBottom: 4 }}>
      {/* Month nav (only if multiple months) */}
      {months.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <button onClick={() => setVisibleIdx(Math.max(0, visibleIdx - 1))} disabled={visibleIdx === 0} style={{
            width: 36, height: 36, borderRadius: 8, background: "transparent",
            border: "none", color: visibleIdx === 0 ? T.border : T.textDim, cursor: visibleIdx === 0 ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{icon(I.ChevLeft, 14)}</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textDim }}>{MONTH_NAMES[month].slice(0, 3)} {year}</span>
          <button onClick={() => setVisibleIdx(Math.min(months.length - 1, visibleIdx + 1))} disabled={visibleIdx >= months.length - 1} style={{
            width: 36, height: 36, borderRadius: 8, background: "transparent",
            border: "none", color: visibleIdx >= months.length - 1 ? T.border : T.textDim, cursor: visibleIdx >= months.length - 1 ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{icon(I.ChevRight, 14)}</button>
        </div>
      )}
      {months.length === 1 && (
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textDim }}>{MONTH_NAMES[month].slice(0, 3)} {year}</span>
        </div>
      )}
      {/* Day labels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, marginBottom: 2 }}>
        {DAY_LABELS.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: T.textDim, padding: "3px 0" }}>{d}</div>
        ))}
      </div>
      {/* Day grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} style={{ height: 36 }} />)}
        {Array.from({ length: dim }, (_, i) => i + 1).map((day) => {
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const date = new Date(year, month, day);
          const inSeason = dateInRange(date, sDate, eDate);
          const isStart = selectedStart === dateStr;
          const isEnd = selectedEnd === dateStr;
          const isInRange = selectedStart && selectedEnd && dateStr >= selectedStart && dateStr <= selectedEnd;

          return (
            <button key={day} disabled={!inSeason}
              onClick={() => handleDayClick(dateStr)}
              style={{
                height: 36, borderRadius: isStart || isEnd ? 8 : isInRange ? 3 : 8,
                border: isStart || isEnd ? `2px solid ${T.accent}` : "1px solid transparent",
                background: isStart || isEnd ? T.accent : isInRange ? T.accentBg2 : "transparent",
                color: isStart || isEnd ? "#fff" : !inSeason ? T.textDim : T.text,
                cursor: !inSeason ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: isStart || isEnd ? 700 : 500,
                opacity: !inSeason ? 0.3 : 1,
                padding: 0,
              }}
            >{day}</button>
          );
        })}
      </div>
    </div>
  );
}

function SpeciesDetailView({ sp, isAdded, myHunts, addHunt, removeHunt, goToBuy, activeState, stateName, fmtD, onBack, onGoToCalendar, onAddMore }) {
  const [showAddedPrompt, setShowAddedPrompt] = useState(false);
  // Track which seasons are checked and their selected dates
  const [seasonSelections, setSeasonSelections] = useState(() => {
    // Pre-populate from existing myHunts entries for this species
    const sel = {};
    myHunts.forEach((h) => {
      if (h.speciesId === sp.id) {
        sel[h.seasonLabel] = { checked: true, start: h.start || null, end: h.end || null };
      }
    });
    return sel;
  });

  const seasons = sp.seasonDates || [];

  const toggleSeason = (label) => {
    setSeasonSelections((prev) => {
      const next = { ...prev };
      if (next[label]?.checked) {
        // Uncheck — clear dates
        delete next[label];
      } else {
        next[label] = { checked: true, start: null, end: null };
      }
      return next;
    });
  };

  const clearSeason = (label, e) => {
    e.stopPropagation();
    setSeasonSelections((prev) => {
      const next = { ...prev };
      delete next[label];
      return next;
    });
  };

  const setSeasonDates = (label, start, end) => {
    setSeasonSelections((prev) => ({
      ...prev,
      [label]: { checked: true, start, end },
    }));
  };

  // Determine which seasons have complete selections (checked + both dates)
  const completedSelections = Object.entries(seasonSelections).filter(
    ([, val]) => val.checked && val.start && val.end
  );

  const handleAddToPlan = () => {
    completedSelections.forEach(([label, val]) => {
      addHunt({
        speciesId: sp.id,
        seasonLabel: label,
        start: val.start,
        end: val.end,
        state: activeState,
      });
    });
    setShowAddedPrompt(true);
  };

  const handleRemoveAll = () => {
    removeHunt(sp.id);
    setSeasonSelections({});
  };

  // Post-add confirmation screen
  if (showAddedPrompt) {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <div style={{ textAlign: "center", padding: "40px 0 24px" }}>
          <div style={{
            width: 64, height: 64, borderRadius: 32, background: T.accentBg2,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px", color: T.accent,
          }}>{icon(I.Check, 28)}</div>
          <h2 style={{ fontFamily: fontD, fontSize: 24, fontWeight: 700, margin: "0 0 8px" }}>
            Added to Hunt Plan!
          </h2>
          <div style={{ fontSize: R.subSize, color: T.textMuted, marginBottom: 4 }}>
            <AnimalIcon emoji={sp.icon} size={16} style={{ verticalAlign: "middle" }} /> {sp.name}
          </div>
          <div style={{ fontSize: R.smallSize, color: T.textDim }}>
            {completedSelections.length} season{completedSelections.length !== 1 ? "s" : ""} added
          </div>
        </div>
        <div style={{ fontSize: R.subSize, fontWeight: 600, color: T.text, marginBottom: 12, textAlign: "center" }}>
          What do you want to do next?
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Btn full variant="primary" onClick={onAddMore}>
            {icon(I.Plus, 16)} Add More Hunts
          </Btn>
          <Btn full variant="secondary" onClick={() => {
            const firstStart = completedSelections.length > 0 ? completedSelections[0][1].start : null;
            onGoToCalendar(firstStart);
          }}>
            {icon(I.Calendar, 16)} See Calendar
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter" style={{ padding: R.pagePad }}>
      <SubNav onBack={onBack} title="Season Details" />
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <AnimalIcon emoji={sp.icon} size={56} />
        <h2 style={{ fontFamily: fontD, fontSize: 26, fontWeight: 700, margin: "10px 0 6px" }}>{sp.name}</h2>
        <div style={{ fontSize: R.subSize, color: T.textMuted }}>{stateName} &bull; {sp.season}</div>
        <div style={{ marginTop: 6 }}><ObtainBadge obtain={sp.obtain} /></div>
      </div>

      {/* Season accordion rows */}
      {seasons.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Label>Season Windows</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
            {seasons.map((sd) => {
              const sel = seasonSelections[sd.label];
              const isChecked = sel?.checked;
              return (
                <div key={sd.label} style={{
                  borderRadius: 10,
                  border: isChecked ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                  background: isChecked ? T.accentBg : T.bg,
                  overflow: "hidden",
                }}>
                  {/* Row header */}
                  <button onClick={() => toggleSeason(sd.label)} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                    width: "100%", cursor: "pointer", fontFamily: font, textAlign: "left",
                    background: "transparent", border: "none",
                  }}>
                    {/* Checkbox */}
                    <div style={{
                      width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                      background: isChecked ? T.accent : "transparent",
                      border: isChecked ? "none" : `2px solid ${T.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff",
                    }}>{isChecked && icon(I.Check, 13)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: R.subSize, fontWeight: 600, color: T.text }}>{sd.label}</div>
                      <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                        {fmtD(sd.start)} – {fmtD(sd.end)}
                      </div>
                      {/* Your Dates summary in header */}
                      {sel?.start && sel?.end && (
                        <div style={{ fontSize: R.smallSize, color: T.accent, fontWeight: 600, marginTop: 2 }}>
                          Your Dates: {fmtD(sel.start)} – {fmtD(sel.end)}
                        </div>
                      )}
                    </div>
                    {/* State badge */}
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: "3px 8px", borderRadius: 5,
                      background: `${SUPPORTED_STATES[activeState]?.color || T.accent}18`,
                      color: SUPPORTED_STATES[activeState]?.color || T.accent,
                      letterSpacing: "0.04em",
                    }}>{activeState}</span>
                    {/* Clear button */}
                    {isChecked && (
                      <button onClick={(e) => clearSeason(sd.label, e)} style={{
                        width: 36, height: 36, borderRadius: 8, background: T.bg,
                        border: `1px solid ${T.border}`, color: T.textDim, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>{icon(I.X, 14)}</button>
                    )}
                  </button>

                  {/* Expanded inline mini calendar */}
                  {isChecked && (
                    <div style={{ padding: "0 12px 10px" }}>
                      <SeasonMiniCalendar
                        seasonStart={sd.start}
                        seasonEnd={sd.end}
                        selectedStart={sel?.start}
                        selectedEnd={sel?.end}
                        onSelect={(start, end) => setSeasonDates(sd.label, start, end)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Planned dates summary */}
      {completedSelections.length > 0 && (
        <div style={{
          marginBottom: 16, padding: "10px 14px", borderRadius: 10,
          background: T.accentBg, border: `1px solid ${T.accent}30`,
        }}>
          <div style={{ fontSize: R.smallSize, fontWeight: 700, color: T.accent, marginBottom: 4 }}>
            Your Planned Dates
          </div>
          {completedSelections.map(([label, val]) => (
            <div key={label} style={{ fontSize: R.subSize, fontWeight: 600, marginBottom: 2 }}>
              {label}: {fmtD(val.start)} – {fmtD(val.end)}
            </div>
          ))}
        </div>
      )}

      {/* Bag limit / possession cards */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <Card style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: R.labelSize, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Bag Limit</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fontD, color: T.accent }}>{sp.bag}</div>
          <div style={{ fontSize: R.labelSize, color: T.textDim }}>{/per (tag|permit|season|year|license)|No limit|Varies/i.test(sp.bag) ? "" : "daily"}</div>
        </Card>
        <Card style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: R.labelSize, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Possession</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fontD, color: T.goldText }}>{sp.poss}</div>
          <div style={{ fontSize: R.labelSize, color: T.textDim }}>total</div>
        </Card>
      </div>

      {/* Notes */}
      {sp.notes && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.6 }}>{sp.notes}</div>
        </Card>
      )}

      {/* Non-sticky action buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 72 }}>
        {isAdded && (
          <Btn full variant="secondary" onClick={handleRemoveAll}>
            {icon(I.Check, 16)} In Your Plan — Remove All
          </Btn>
        )}
        <Btn full variant="ghost" onClick={goToBuy}>Get Licensed {icon(I.Arrow, 14)}</Btn>
      </div>

      {/* Sticky Add Hunt button */}
      <div style={{
        position: "sticky", bottom: 0, left: 0, right: 0,
        padding: "12px 0 16px", background: `linear-gradient(transparent, ${T.bg} 8px)`,
        zIndex: 5,
      }}>
        <Btn full variant="primary" onClick={handleAddToPlan}
          disabled={completedSelections.length === 0}
          style={{
            background: completedSelections.length === 0 ? T.textDim : undefined,
          }}
        >
          {icon(I.Plus, 16)} Add to Hunt Plan
        </Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// TAB 3: CALENDAR — COMPACT GRID + CHRONOLOGICAL STREAM
// ═══════════════════════════════════════════

function CalendarTab({ myHunts, addHunt, removeHunt, scrollTop, goToBuy, stateData, activeState, selectState, stateLoading }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [showAddHunt, setShowAddHunt] = useState(false);
  const [speciesDetail, setSpeciesDetail] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(viewYear);
  // Multi-select: set of active state codes. Empty set = show all.
  const [selectedStates, setSelectedStates] = useState(() => new Set([activeState].filter(Boolean)));
  // Track loaded state data for all selected states
  const [allStateData, setAllStateData] = useState(() => {
    const init = {};
    if (activeState && stateData) init[activeState] = stateData;
    return init;
  });
  const streamRef = useRef(null);
  const streamItemRefs = useRef({});
  const huntPlanRefs = useRef({});
  const calGridRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Keep allStateData in sync when the prop stateData changes
  useEffect(() => {
    if (activeState && stateData) {
      setAllStateData((prev) => {
        if (prev[activeState] === stateData) return prev;
        return { ...prev, [activeState]: stateData };
      });
    }
  }, [activeState, stateData]);

  // Load data for all selected states that haven't been loaded yet
  useEffect(() => {
    let cancelled = false;
    selectedStates.forEach((code) => {
      if (!allStateData[code]) {
        loadStateData(code).then((d) => {
          if (!cancelled) {
            setAllStateData((prev) => ({ ...prev, [code]: d }));
          }
        }).catch(() => {});
      }
    });
    return () => { cancelled = true; };
  }, [selectedStates, allStateData]);

  // Aggregate SPECIES, KEY_DATES, CAT_COLORS_MAP across all selected states
  const stateCodesToShow = selectedStates.size > 0 ? [...selectedStates] : [activeState];
  const SPECIES = useMemo(() => {
    const result = [];
    const seen = new Set();
    stateCodesToShow.forEach((code) => {
      const sd = allStateData[code];
      if (!sd) return;
      (sd.species || []).forEach((sp) => {
        const key = `${code}:${sp.id}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ ...sp, _stateCode: code });
        }
      });
    });
    return result;
  }, [stateCodesToShow.join(","), allStateData]);

  const KEY_DATES = useMemo(() => {
    const result = [];
    stateCodesToShow.forEach((code) => {
      const sd = allStateData[code];
      if (!sd) return;
      (sd.stateConfig?.keyDates || []).forEach((kd) => {
        result.push({ ...kd, state: kd.state || code });
      });
    });
    return result;
  }, [stateCodesToShow.join(","), allStateData]);

  const CAT_COLORS_MAP = useMemo(() => {
    const colors = {};
    stateCodesToShow.forEach((code) => {
      const sd = allStateData[code];
      if (!sd) return;
      (sd.stateConfig?.categories || []).forEach((c) => { colors[c.id] = c.color; });
    });
    return colors;
  }, [stateCodesToShow.join(","), allStateData]);

  const stateName = stateData?.stateConfig?.stateName || SUPPORTED_STATES[activeState]?.name || "";

  // Categories for the Add Hunt species browser (aggregated across selected states)
  const CATEGORIES = useMemo(() => {
    const seen = new Set();
    const result = [];
    stateCodesToShow.forEach((code) => {
      const sd = allStateData[code];
      if (!sd) return;
      (sd.stateConfig?.categories || []).forEach((c) => {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          result.push(c);
        }
      });
    });
    return result;
  }, [stateCodesToShow.join(","), allStateData]);

  // Filtered myHunts based on selected states
  const filteredHunts = useMemo(() => {
    if (selectedStates.size === 0) return myHunts;
    return myHunts.filter((h) => selectedStates.has(h.state));
  }, [myHunts, selectedStates]);

  // Find species matching hunt (prefer same state)
  const findSpeciesForHunt = useCallback((h) => {
    return SPECIES.find((s) => s.id === h.speciesId && s._stateCode === h.state)
      || SPECIES.find((s) => s.id === h.speciesId);
  }, [SPECIES]);

  // Unique species in filtered hunts (for legend)
  const mySpecies = useMemo(() => {
    const seen = new Set();
    const result = [];
    filteredHunts.forEach((h) => {
      const key = `${h.state}:${h.speciesId}`;
      if (!seen.has(key)) {
        seen.add(key);
        const sp = findSpeciesForHunt(h);
        if (sp) result.push(sp);
      }
    });
    return result;
  }, [filteredHunts, findSpeciesForHunt]);

  // Navigate months — read viewMonth/viewYear from closure (deps keep them fresh)
  const goPrevMonth = useCallback(() => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else { setViewMonth(viewMonth - 1); }
  }, [viewMonth, viewYear]);
  const goNextMonth = useCallback(() => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else { setViewMonth(viewMonth + 1); }
  }, [viewMonth, viewYear]);
  const goToToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };
  const jumpToMonth = (month, year) => { setViewMonth(month); setViewYear(year); setShowMonthPicker(false); };

  // Swipe handling for month navigation
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Only swipe if horizontal movement > 50px and greater than vertical
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNextMonth();
      else goPrevMonth();
    }
  }, [goNextMonth, goPrevMonth]);

  // Compute weeks for the viewed month
  const weeks = useMemo(() => {
    const dim = getDaysInMonth(viewYear, viewMonth);
    const fd = getFirstDayOfWeek(viewYear, viewMonth);
    const result = [];
    let week = [];
    for (let i = 0; i < fd; i++) week.push(null);
    for (let d = 1; d <= dim; d++) {
      week.push(d);
      if (week.length === 7) { result.push(week); week = []; }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      result.push(week);
    }
    return result;
  }, [viewYear, viewMonth]);

  // Compute spanning event bars per week row
  const huntBarsPerWeek = useMemo(() => {
    const result = {};
    // Assign colors: for each species, each season gets a distinct shade
    const speciesSeasonIdx = {};
    const colorForEntry = (h) => {
      const baseColor = SUPPORTED_STATES[h.state]?.color || "#888";
      const key = `${h.state}:${h.speciesId}`;
      if (!speciesSeasonIdx[key]) speciesSeasonIdx[key] = {};
      if (speciesSeasonIdx[key][h.seasonLabel] === undefined) {
        speciesSeasonIdx[key][h.seasonLabel] = Object.keys(speciesSeasonIdx[key]).length;
      }
      const idx = speciesSeasonIdx[key][h.seasonLabel];
      const shifts = [0, 30, -25, 55, -40];
      return adjustColor(baseColor, shifts[idx % shifts.length]);
    };

    weeks.forEach((week, weekIdx) => {
      const bars = [];
      filteredHunts.forEach((h) => {
        if (!h.start || !h.end) return;
        const sp = findSpeciesForHunt(h);
        if (!sp) return;
        const hStart = parseDate(h.start);
        const hEnd = parseDate(h.end);

        let startCol = -1, endCol = -1;
        for (let c = 0; c < 7; c++) {
          if (week[c] === null) continue;
          const d = new Date(viewYear, viewMonth, week[c]);
          if (d >= hStart && d <= hEnd) {
            if (startCol === -1) startCol = c;
            endCol = c;
          }
        }

        if (startCol !== -1) {
          // Does the bar's actual start/end fall within this week?
          const hStartDay = hStart.getMonth() === viewMonth && hStart.getFullYear() === viewYear ? hStart.getDate() : null;
          const hEndDay = hEnd.getMonth() === viewMonth && hEnd.getFullYear() === viewYear ? hEnd.getDate() : null;
          const isStartInWeek = hStartDay !== null && week.includes(hStartDay);
          const isEndInWeek = hEndDay !== null && week.includes(hEndDay);

          bars.push({
            startCol,
            endCol,
            species: sp,
            seasonLabel: h.seasonLabel,
            color: colorForEntry(h),
            roundLeft: isStartInWeek,
            roundRight: isEndInWeek,
            state: h.state,
          });
        }
      });
      result[weekIdx] = bars;
    });
    return result;
  }, [weeks, filteredHunts, findSpeciesForHunt, CAT_COLORS_MAP, viewYear, viewMonth]);

  // Build the chronological stream — interleave KEY_DATES and hunt entries
  const streamItems = useMemo(() => {
    const items = [];
    const rangeStart = new Date(viewYear, viewMonth, 1);
    const dim = getDaysInMonth(viewYear, viewMonth);
    const rangeEnd = new Date(viewYear, viewMonth, dim);

    // Add key dates in this month (filtered by state)
    KEY_DATES.forEach((kd) => {
      if (selectedStates.size > 0 && !selectedStates.has(kd.state)) return;
      const d = parseDate(kd.date);
      if (d >= rangeStart && d <= rangeEnd) {
        items.push({
          type: "keydate",
          date: kd.date,
          dateObj: d,
          day: d.getDate(),
          data: kd,
        });
      }
    });

    // Add planned hunt entries that overlap this month
    filteredHunts.forEach((h) => {
      if (!h.start || !h.end) return;
      const sp = findSpeciesForHunt(h);
      if (!sp) return;
      const stateColor = SUPPORTED_STATES[h.state]?.color || T.accent;
      const s = parseDate(h.start);
      const e = parseDate(h.end);
      if (s <= rangeEnd && e >= rangeStart) {
        const displayDate = s < rangeStart
          ? `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-01`
          : h.start;
        items.push({
          type: "custom-range",
          date: displayDate,
          dateObj: s < rangeStart ? rangeStart : s,
          day: s < rangeStart ? 1 : s.getDate(),
          species: sp,
          color: stateColor,
          label: h.seasonLabel,
          startDate: h.start,
          endDate: h.end,
          huntState: h.state,
        });
      }
    });

    // Sort by date, then key dates first (deadlines most urgent)
    items.sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      if (a.type === "keydate" && b.type !== "keydate") return -1;
      if (a.type !== "keydate" && b.type === "keydate") return 1;
      if (a.type === "keydate" && b.type === "keydate") {
        if (a.data.type === "deadline" && b.data.type !== "deadline") return -1;
        if (a.data.type !== "deadline" && b.data.type === "deadline") return 1;
      }
      return 0;
    });

    return items;
  }, [viewYear, viewMonth, KEY_DATES, filteredHunts, findSpeciesForHunt, CAT_COLORS_MAP, selectedStates]);

  // Scroll stream to a specific day
  const scrollStreamToDay = useCallback((day) => {
    setShowMonthPicker(false);
    // Find the first stream item on or after this day
    const targetItem = streamItems.find((item) => item.day >= day);
    if (targetItem) {
      const idx = streamItems.indexOf(targetItem);
      const refKey = `${targetItem.type}-${targetItem.date}-${targetItem.type === "keydate" ? targetItem.data.label : targetItem.species?.id}-${targetItem.label || idx}`;
      const el = streamItemRefs.current[refKey];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    // If no exact match, scroll to nearest
    if (streamRef.current) {
      const children = streamRef.current.children;
      for (let i = 0; i < children.length; i++) {
        const dayAttr = children[i].getAttribute("data-day");
        if (dayAttr && parseInt(dayAttr) >= day) {
          children[i].scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }
    }
  }, [streamItems]);

  // Scroll to a specific hunt in the My Hunt Plan list
  const scrollToHuntPlan = useCallback((speciesId, seasonLabel) => {
    const key = `${speciesId}-${seasonLabel}`;
    const el = huntPlanRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief highlight flash
      el.style.transition = "box-shadow 0.3s ease";
      el.style.boxShadow = `0 0 0 2px ${T.accent}, 0 4px 14px rgba(0,0,0,0.1)`;
      setTimeout(() => { el.style.boxShadow = ""; }, 1500);
    }
  }, []);

  // Format date nicely
  const fmtDate = (dateStr) => new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const fmtDateFull = (dateStr) => new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();

  if (stateLoading && !stateData) {
    return (
      <div className="page-enter" style={{ padding: R.pagePad, textAlign: "center" }}>
        <div style={{ padding: "40px 0", color: T.textDim }}>Loading...</div>
      </div>
    );
  }

  // Species detail view — interactive season accordion with inline date pickers
  if (speciesDetail) {
    const sp = speciesDetail;
    const isAdded = myHunts.some((h) => h.speciesId === sp.id);
    const fmtD = (s) => new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

    return (
      <SpeciesDetailView
        sp={sp}
        isAdded={isAdded}
        myHunts={myHunts}
        addHunt={addHunt}
        removeHunt={removeHunt}
        goToBuy={goToBuy}
        activeState={activeState}
        stateName={stateName}
        fmtD={fmtD}
        onBack={() => setSpeciesDetail(null)}
        onGoToCalendar={(startDate) => {
          setSpeciesDetail(null); setShowAddHunt(false);
          if (startDate) {
            const d = new Date(startDate + "T12:00:00");
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
            // Scroll to the hunt in the plan list after render
            setTimeout(() => {
              const huntEntry = myHunts.find((h) => h.speciesId === sp.id && h.start === startDate);
              if (huntEntry) {
                const key = `${huntEntry.speciesId}-${huntEntry.seasonLabel}`;
                const el = huntPlanRefs.current[key];
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.style.transition = "box-shadow 0.3s ease";
                  el.style.boxShadow = `0 0 0 2px ${T.accent}, 0 4px 14px rgba(0,0,0,0.1)`;
                  setTimeout(() => { el.style.boxShadow = ""; }, 1500);
                }
              }
            }, 300);
          } else {
            scrollTop();
          }
        }}
        onAddMore={() => { setSpeciesDetail(null); }}
      />
    );
  }

  // Add Hunt browser — preserved exactly
  if (showAddHunt) {
    const filteredSpecies = catFilter === "all" ? SPECIES : SPECIES.filter((s) => s.cat === catFilter);
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 0 12px", borderBottom: `1px solid ${T.border}`, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setShowAddHunt(false)} aria-label="Close add hunt" className="nav-btn" style={{
              width: R.iconBtn, height: R.iconBtn, borderRadius: 10, background: T.bg,
              border: `1px solid ${T.border}`, color: T.textMuted, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{icon(I.X, 16)}</button>
            <span style={{ fontSize: R.bodySize, fontWeight: 600, fontFamily: font }}>Add Hunt</span>
          </div>
          <span style={{ fontSize: R.smallSize, color: T.accent, fontWeight: 700 }}>{myHunts.length} in plan</span>
        </div>
        <div className="scroll-horizontal" style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
          {Object.entries(SUPPORTED_STATES).map(([code, st]) => (
            <button key={code} className="state-btn" onClick={() => { selectState(code); setCatFilter("all"); }}
              style={{
                padding: "clamp(8px, 2vw, 12px) clamp(12px, 3vw, 18px)", borderRadius: 8,
                fontSize: R.smallSize, fontWeight: 700, fontFamily: font, whiteSpace: "nowrap",
                border: activeState === code ? `2px solid ${st.color}` : `1px solid ${T.border}`,
                background: activeState === code ? `${st.color}12` : "transparent",
                color: activeState === code ? st.color : T.textDim, cursor: "pointer", minHeight: R.tapSmall,
              }}>{code}</button>
          ))}
        </div>
        <div className="scroll-horizontal" style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
          {[{ id: "all", label: "All" }, ...CATEGORIES].map((cat) => (
            <button key={cat.id} className="filter-pill" onClick={() => setCatFilter(cat.id)}
              style={{
                padding: "clamp(8px, 2vw, 12px) clamp(12px, 3vw, 18px)", borderRadius: 8,
                fontSize: R.smallSize, fontWeight: 700, fontFamily: font, whiteSpace: "nowrap",
                border: catFilter === cat.id ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
                background: catFilter === cat.id ? T.accentBg2 : T.card,
                color: catFilter === cat.id ? T.accent : T.textDim, cursor: "pointer", minHeight: R.tapSmall,
              }}>{cat.icon ? <><AnimalIcon emoji={cat.icon} size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /></> : ""}{cat.label}</button>
          ))}
        </div>
        <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 40 }}>
          {filteredSpecies.map((sp) => {
            const hasAny = myHunts.some((h) => h.speciesId === sp.id);
            return (
              <div key={sp.id} className="species-card" onClick={() => setSpeciesDetail(sp)} style={{
                padding: "14px 16px", borderRadius: 14, cursor: "pointer",
                border: hasAny ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                background: hasAny ? T.accentBg : T.card, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <AnimalIcon emoji={sp.icon} size={20} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: R.subSize, fontWeight: 600 }}>{sp.name}</span>
                      <ObtainBadge obtain={sp.obtain} />
                    </div>
                    <div style={{ fontSize: R.smallSize, color: T.textDim, marginTop: 1 }}>{sp.season}</div>
                  </div>
                  {hasAny && (
                    <div style={{
                      width: R.iconBtn, height: R.iconBtn, borderRadius: 9,
                      background: T.accent, border: "none", color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>{icon(I.Check, 16)}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // MAIN CALENDAR VIEW — Compact Grid + Stream
  // ═══════════════════════════════════════════

  return (
    <div className="page-enter" style={{ padding: R.pagePad }}>
      {/* ─── MONTH GRID ─── */}
      <div ref={calGridRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{
        background: T.card, borderRadius: 16, border: `1px solid ${T.border}`,
        padding: "18px 16px 18px", marginBottom: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)", position: "relative",
      }}>
        {/* Month navigation row with Add Hunt pill */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
            <button onClick={goPrevMonth} aria-label="Previous month" className="nav-btn" style={{
              width: 44, height: 44, borderRadius: 10, background: "transparent",
              border: "none", color: T.textMuted, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{icon(I.ChevLeft, 16)}</button>
            <button onClick={() => { if (!showMonthPicker) setPickerYear(viewYear); setShowMonthPicker(!showMonthPicker); }} style={{
              background: "none", border: "none", cursor: "pointer", padding: "4px 8px",
              display: "flex", alignItems: "baseline", justifyContent: "center", gap: 5, borderRadius: 8,
              flex: 1,
            }}>
              <span style={{ fontFamily: fontD, fontSize: "clamp(18px, 5vw, 22px)", fontWeight: 700, color: T.text }}>
                {MONTH_NAMES[viewMonth]}
              </span>
              <span style={{ fontFamily: fontD, fontSize: "clamp(18px, 5vw, 22px)", fontWeight: 700, color: T.textDim }}>
                {viewYear}
              </span>
              {isCurrentMonth && (
                <span style={{
                  fontSize: R.tinySize, fontWeight: 700, padding: "2px 6px", borderRadius: 5,
                  background: T.accentBg2, color: T.accent, marginLeft: 2,
                }}>NOW</span>
              )}
              <span style={{ color: T.textDim, fontSize: 10, marginLeft: 2 }}>{showMonthPicker ? "\u25B2" : "\u25BC"}</span>
            </button>
            <button onClick={goNextMonth} aria-label="Next month" className="nav-btn" style={{
              width: 44, height: 44, borderRadius: 10, background: "transparent",
              border: "none", color: T.textMuted, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{icon(I.ChevRight, 16)}</button>
          </div>

          {/* Add Hunt pill */}
          <button
            onClick={() => { setCatFilter("all"); setShowAddHunt(true); }}
            aria-label="Add hunt to calendar"
            className="animate-scale-in"
            style={{
              padding: "10px 16px", borderRadius: 20,
              background: T.accent, color: "#fff", border: "none",
              fontSize: R.smallSize, fontWeight: 700, fontFamily: font,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
              lineHeight: 1, minHeight: R.tapSmall,
            }}
          >
            {icon(I.Plus, 14)} Add
          </button>
        </div>

        {/* Jump-to-month picker overlay + backdrop */}
        {showMonthPicker && (
          <>
            <div onClick={() => setShowMonthPicker(false)} style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9,
            }} />
            <div style={{
              position: "absolute", top: 56, left: 0, right: 0, zIndex: 10,
              background: T.card, borderRadius: 12, border: `1px solid ${T.border}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "12px 14px",
              margin: "0 8px",
            }}>
              {/* Year navigation */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <button onClick={() => setPickerYear((y) => y - 1)} className="nav-btn" style={{
                  width: 44, height: 44, borderRadius: 10, background: T.bg,
                  border: `1px solid ${T.border}`, color: T.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{icon(I.ChevLeft, 14)}</button>
                <span style={{ fontFamily: fontD, fontSize: R.bodySize, fontWeight: 700, color: T.text }}>{pickerYear}</span>
                <button onClick={() => setPickerYear((y) => y + 1)} className="nav-btn" style={{
                  width: 44, height: 44, borderRadius: 10, background: T.bg,
                  border: `1px solid ${T.border}`, color: T.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{icon(I.ChevRight, 14)}</button>
              </div>
              {/* Month grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {MONTH_NAMES.map((name, mi) => {
                  const isCurrent = mi === today.getMonth() && pickerYear === today.getFullYear();
                  const isSelected = mi === viewMonth && pickerYear === viewYear;
                  return (
                    <button key={mi} onClick={() => jumpToMonth(mi, pickerYear)}
                      style={{
                        padding: "12px 4px", borderRadius: 10, border: "none",
                        background: isSelected ? T.accent : isCurrent ? T.accentBg2 : T.bg,
                        color: isSelected ? "#fff" : isCurrent ? T.accent : T.text,
                        fontSize: R.smallSize, fontWeight: isSelected || isCurrent ? 700 : 500,
                        fontFamily: font, cursor: "pointer", minHeight: 44,
                      }}
                    >{name.slice(0, 3)}</button>
                  );
                })}
              </div>
              {/* Today shortcut */}
              <button onClick={() => { goToToday(); setShowMonthPicker(false); }} style={{
                marginTop: 10, width: "100%", padding: "10px 0", borderRadius: 10,
                border: `1px solid ${T.accent}30`, background: T.accentBg,
                color: T.accent, fontSize: R.smallSize, fontWeight: 700,
                fontFamily: font, cursor: "pointer",
              }}>Today</button>
            </div>
          </>
        )}

        {/* Day labels */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, marginBottom: 4 }}>
          {DAY_LABELS.map((d) => (
            <div key={d} style={{
              textAlign: "center", fontSize: R.smallSize, fontWeight: 700,
              color: T.textDim, padding: "6px 0", textTransform: "uppercase", letterSpacing: "0.06em",
            }}>{d}</div>
          ))}
        </div>

        {/* Calendar grid — day numbers + spanning event bars */}
        {weeks.map((week, weekIdx) => {
          const bars = huntBarsPerWeek[weekIdx] || [];
          return (
            <div key={weekIdx} style={{ marginBottom: bars.length > 0 ? 2 : 0 }}>
              {/* Day numbers row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0 }}>
                {week.map((day, colIdx) => {
                  if (day === null) return <div key={`e-${weekIdx}-${colIdx}`} style={{ height: "clamp(48px, 13vw, 60px)" }} />;
                  const date = new Date(viewYear, viewMonth, day);
                  const isDayToday = sameDay(date, today);
                  const isPast = date < today && !isDayToday;
                  const hasKeyDate = KEY_DATES.some((kd) => {
                    if (selectedStates.size > 0 && !selectedStates.has(kd.state)) return false;
                    return sameDay(parseDate(kd.date), date);
                  });
                  return (
                    <button key={day} className="calendar-day"
                      onClick={() => scrollStreamToDay(day)}
                      style={{
                        height: "clamp(48px, 13vw, 60px)", borderRadius: 6,
                        border: isDayToday ? `2px solid ${T.accent}` : "1px solid transparent",
                        background: isDayToday ? T.accent : "transparent",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        position: "relative", margin: "0 1px", padding: 0,
                      }}
                    >
                      <span style={{
                        fontSize: R.smallSize, fontWeight: isDayToday ? 700 : 500,
                        color: isDayToday ? "#fff" : isPast ? T.textDim : T.text, lineHeight: 1,
                      }}>{day}</span>
                      {hasKeyDate && (
                        <div style={{
                          position: "absolute", top: 1, right: 1,
                          width: 6, height: 6, borderRadius: 3,
                          background: isDayToday ? "#fff" : T.red,
                          border: isDayToday ? "none" : `1px solid ${T.card}`,
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Spanning event bars */}
              {bars.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 1, paddingBottom: 2 }}>
                  {bars.map((bar, bi) => {
                    const spanPct = ((bar.endCol - bar.startCol + 1) / 7) * 100;
                    const leftPct = (bar.startCol / 7) * 100;
                    const isPast = new Date(viewYear, viewMonth, weeks[weekIdx]?.[bar.endCol] || 1) < today;
                    return (
                      <div key={bi} className="season-bar" onClick={() => scrollToHuntPlan(bar.species.id, bar.seasonLabel)} style={{
                        marginLeft: `${leftPct}%`,
                        width: `${spanPct}%`,
                        height: 38,
                        background: bar.color,
                        borderRadius: `${bar.roundLeft ? 6 : 0}px ${bar.roundRight ? 6 : 0}px ${bar.roundRight ? 6 : 0}px ${bar.roundLeft ? 6 : 0}px`,
                        display: "flex", flexDirection: "column", justifyContent: "center",
                        paddingLeft: bar.roundLeft ? 5 : 2, paddingRight: 2,
                        overflow: "hidden",
                        opacity: isPast ? 0.5 : 0.9,
                        cursor: "pointer",
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: "#fff",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          lineHeight: 1.1, letterSpacing: "0.02em",
                        }}>
                          {bar.species.name}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.85)",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          lineHeight: 1.1, letterSpacing: "0.02em", marginTop: 1,
                        }}>
                          {bar.seasonLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── STATE FILTER CHIPS ─── */}
      <div className="scroll-horizontal" style={{
        display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4,
        msOverflowStyle: "none", scrollbarWidth: "none",
      }}>
        {Object.entries(SUPPORTED_STATES).map(([code, st]) => {
          const isActive = selectedStates.has(code);
          return (
            <button key={code} onClick={() => {
              setSelectedStates((prev) => {
                const next = new Set(prev);
                if (next.has(code)) { next.delete(code); } else { next.add(code); }
                return next;
              });
              // Also load this state's data for key dates & species catalog
              if (!selectedStates.has(code)) selectState(code);
            }} style={{
              padding: "8px 16px", borderRadius: 20, fontSize: R.smallSize, fontWeight: 700,
              fontFamily: font, whiteSpace: "nowrap", cursor: "pointer", minHeight: R.tapSmall,
              border: isActive ? `2px solid ${st.color}` : `1px solid ${T.border}`,
              background: isActive ? `${st.color}18` : T.bg,
              color: isActive ? st.color : T.textDim,
              transition: "all 0.15s ease",
            }}>
              {code}
            </button>
          );
        })}
      </div>

      {/* ─── MY HUNT PLAN LIST ─── */}
      {filteredHunts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Label>My Hunt Plan</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filteredHunts.map((h, i) => {
              const sp = findSpeciesForHunt(h);
              if (!sp) return null;
              const huntStateColor = SUPPORTED_STATES[h.state]?.color || T.accent;
              return (
                <div key={`${h.speciesId}-${h.seasonLabel}-${i}`}
                  ref={(el) => { huntPlanRefs.current[`${h.speciesId}-${h.seasonLabel}`] = el; }}
                  onClick={() => setSpeciesDetail(sp)}
                  style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                  borderRadius: 12, background: T.card, border: `1px solid ${T.border}`,
                  position: "relative", overflow: "hidden", cursor: "pointer",
                }}>
                  {/* State color bar */}
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
                    background: huntStateColor, borderRadius: "10px 0 0 10px",
                  }} />
                  <AnimalIcon emoji={sp.icon} size={20} style={{ paddingLeft: 6 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: R.subSize, fontWeight: 600, color: T.text }}>
                      {sp.name} — {h.seasonLabel}
                    </div>
                    <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                      {h.state && <>{h.state} &bull; </>}
                      {h.start && h.end ? (
                        <>{fmtDate(h.start)} – {fmtDate(h.end)}</>
                      ) : (
                        <span style={{ fontStyle: "italic" }}>No dates selected</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeHunt(h.speciesId, h.seasonLabel)} style={{
                    width: 38, height: 38, borderRadius: 9, background: T.bg,
                    border: `1px solid ${T.border}`, color: T.textDim, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>{icon(I.X, 14)}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── CHRONOLOGICAL STREAM ─── */}
      <div ref={streamRef}>
        {/* Stream header with legend */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingTop: 4 }}>
          <div style={{ fontSize: R.smallSize, color: T.textMuted, fontWeight: 600 }}>
            {MONTH_NAMES[viewMonth]} {viewYear}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {stateCodesToShow.map((code) => (
              <div key={code} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: R.tinySize, color: T.textDim }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: SUPPORTED_STATES[code]?.color || T.accent }} />
                {SUPPORTED_STATES[code]?.name || code}
              </div>
            ))}
          </div>
        </div>

        {/* Stream items */}
        {streamItems.length > 0 ? (
          <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 24 }}>
            {streamItems.map((item, idx) => {
              const refKey = `${item.type}-${item.date}-${item.type === "keydate" ? item.data.label : item.species?.id}-${item.label || idx}`;
              // Show date header if first item on this day
              const showDateHeader = idx === 0 || item.date !== streamItems[idx - 1].date;

              if (item.type === "keydate") {
                const kd = item.data;
                const isDeadline = kd.type === "deadline";
                const isOpener = kd.type === "opener";
                const kdStateColor = SUPPORTED_STATES[kd.state]?.color || (isDeadline ? T.red : T.orange);
                const accentColor = kdStateColor;
                const bgColor = `${kdStateColor}12`;
                const typeLabel = isDeadline ? "DEADLINE" : isOpener ? "OPENER" : "KEY DATE";

                return (
                  <div key={refKey} data-day={item.day}
                    ref={(el) => { streamItemRefs.current[refKey] = el; }}
                  >
                    {showDateHeader && (
                      <div style={{
                        fontSize: R.tinySize, fontWeight: 700, color: T.textDim,
                        textTransform: "uppercase", letterSpacing: "0.08em",
                        padding: "8px 0 4px", marginTop: idx > 0 ? 4 : 0,
                      }}>
                        {fmtDateFull(item.date)}
                      </div>
                    )}
                    <div className="stream-event-card" style={{
                      padding: "14px 16px", borderRadius: 14,
                      background: bgColor, border: `1.5px solid ${accentColor}25`,
                      position: "relative", overflow: "hidden",
                    }}>
                      {/* Urgent left accent bar */}
                      <div style={{
                        position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
                        background: accentColor, borderRadius: "12px 0 0 12px",
                      }} />
                      <div style={{ paddingLeft: 8 }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 6, marginBottom: 4,
                        }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: `${accentColor}20`, color: accentColor,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}>{icon(I.Alert, 11)}</div>
                          <span style={{
                            fontSize: R.tinySize, fontWeight: 800, color: accentColor,
                            textTransform: "uppercase", letterSpacing: "0.1em",
                          }}>{typeLabel}</span>
                          {kd.state && (
                            <span style={{
                              fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4,
                              background: `${SUPPORTED_STATES[kd.state]?.color || T.accent}18`,
                              color: SUPPORTED_STATES[kd.state]?.color || T.accent, letterSpacing: "0.04em",
                            }}>{kd.state}</span>
                          )}
                        </div>
                        <div style={{ fontSize: R.subSize, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                          {kd.label}
                        </div>
                        <div style={{ fontSize: R.smallSize, color: T.textMuted, lineHeight: 1.5 }}>
                          {kd.desc}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // Planned hunt entry card
              if (item.type === "custom-range") {
                const sp = item.species;
                const catColor = item.color;
                return (
                  <div key={refKey} data-day={item.day}
                    ref={(el) => { streamItemRefs.current[refKey] = el; }}
                  >
                    {showDateHeader && (
                      <div style={{
                        fontSize: R.tinySize, fontWeight: 700, color: T.textDim,
                        textTransform: "uppercase", letterSpacing: "0.08em",
                        padding: "8px 0 4px", marginTop: idx > 0 ? 4 : 0,
                      }}>
                        {fmtDateFull(item.date)}
                      </div>
                    )}
                    <div className="stream-season-card" onClick={() => setSpeciesDetail(sp)}
                      style={{
                        padding: "16px 16px", borderRadius: 14,
                        background: `${catColor}10`, border: `1.5px solid ${catColor}30`,
                        cursor: "pointer", position: "relative", overflow: "hidden",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{
                        position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
                        background: catColor, borderRadius: "12px 0 0 12px",
                      }} />
                      <div style={{ paddingLeft: 8, display: "flex", alignItems: "center", gap: 10 }}>
                        <AnimalIcon emoji={sp.icon} size={24} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: R.subSize, fontWeight: 700, color: T.text }}>
                              {sp.name} — {item.label}
                            </span>
                            <span style={{
                              fontSize: R.tinySize, fontWeight: 700,
                              padding: "1px 6px", borderRadius: 4,
                              background: `${catColor}15`, color: catColor,
                            }}>
                              PLANNED
                            </span>
                          </div>
                          <div style={{ fontSize: R.smallSize, color: T.textMuted }}>
                            {item.huntState && <>{item.huntState} &bull; </>}
                            {fmtDate(item.startDate)} – {fmtDate(item.endDate)}
                          </div>
                        </div>
                        <div style={{ color: T.textDim, flexShrink: 0 }}>{icon(I.ChevRight, 14)}</div>
                      </div>
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>
        ) : (
          /* Empty state — no events this month */
          <div className="animate-fade-in-up" style={{
            textAlign: "center", padding: "24px 16px", borderRadius: 12,
            background: T.card, border: `1px solid ${T.border}`,
          }}>
            {myHunts.length === 0 ? (
              <>
                <div style={{ fontSize: R.bodySize, fontWeight: 700, fontFamily: fontD, marginBottom: 4 }}>
                  {icon(I.Crosshair, 18)} Plan Your Season
                </div>
                <div style={{ fontSize: R.smallSize, color: T.textMuted, marginBottom: 12 }}>
                  Add species to see your seasons and deadlines here.
                </div>
                <Btn full onClick={() => { setCatFilter("all"); setShowAddHunt(true); }}>
                  {icon(I.Plus, 16)} Add Your First Hunt
                </Btn>
              </>
            ) : (
              <>
                <div style={{ fontSize: R.subSize, color: T.textDim, marginBottom: 4 }}>
                  No events in {MONTH_NAMES[viewMonth]}
                </div>
                <div style={{ fontSize: R.smallSize, color: T.textDim }}>
                  Try navigating to a different month.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// TAB 4: REGULATIONS
// ═══════════════════════════════════════════

function RegulationsTab({ stateData, activeState, selectState, stateLoading }) {
  const [search, setSearch] = useState("");
  const STATES = Object.entries(SUPPORTED_STATES).map(([id, s]) => ({ id, ...s }));

  // If no state loaded, show state picker
  if (!activeState || !stateData) {
    return (
      <div className="page-enter" style={{ padding: R.pagePad }}>
        <SectionTitle sub="Select a state to view regulations.">
          Regulations
        </SectionTitle>
        <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {STATES.map((s) => (
            <Card key={s.id} onClick={() => selectState(s.id)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: `${s.color}15`, border: `2px solid ${s.color}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: R.subSize, fontWeight: 800, color: s.color, fontFamily: font,
                }}>{s.id}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: R.bodySize, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: R.smallSize, color: T.textDim }}>Brochures & quick links</div>
                </div>
                <div style={{ color: T.textDim }}>{icon(I.ChevRight, 16)}</div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (stateLoading) {
    return (
      <div className="page-enter" style={{ padding: R.pagePad, textAlign: "center" }}>
        <div style={{ padding: "40px 0", color: T.textDim }}>Loading...</div>
      </div>
    );
  }

  const cfg = stateData.stateConfig;
  const stateRegs = cfg.regulations || [];
  const stateLinks = cfg.quickLinks || [];
  const stateColor = cfg.stateColor || SUPPORTED_STATES[activeState]?.color || T.accent;
  const stateName = cfg.stateName;
  const agencyName = cfg.agencyName || "";

  const cats = {};
  stateRegs.forEach((r) => {
    if (!cats[r.cat]) cats[r.cat] = [];
    cats[r.cat].push(r);
  });

  const filtered = search
    ? stateRegs.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    : null;

  return (
    <div className="page-enter" style={{ padding: R.pagePad }}>
      <SectionTitle sub="Official publications and quick links.">
        Regulations
      </SectionTitle>

      {/* State switcher */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
        {STATES.map((s) => (
          <button
            key={s.id}
            onClick={() => { selectState(s.id); setSearch(""); }}
            style={{
              padding: "clamp(10px, 2.5vw, 14px) clamp(14px, 3.5vw, 20px)",
              borderRadius: 10, fontSize: R.smallSize, fontWeight: 700,
              fontFamily: font, cursor: "pointer", whiteSpace: "nowrap",
              border: activeState === s.id ? `2px solid ${s.color}` : `1px solid ${T.border}`,
              background: activeState === s.id ? `${s.color}12` : "transparent",
              color: activeState === s.id ? s.color : T.textDim,
              minHeight: R.tapSmall,
            }}
          >
            {s.id} {s.name}
          </button>
        ))}
      </div>

      <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {stateLinks.map((ql) => (
          <a key={ql.url} href={ql.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 16px", borderRadius: 12,
              background: T.accentBg, border: `1px solid ${T.accent}30`,
            }}>
              <div style={{ color: T.accent }}>{icon(I.Link, 16)}</div>
              <span style={{ fontSize: R.subSize, fontWeight: 600, color: T.accent }}>{ql.label}</span>
            </div>
          </a>
        ))}
      </div>

      <div style={{ position: "relative", marginBottom: 20 }}>
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.textDim }}>
          {icon(I.Search, 16)}
        </div>
        <input
          aria-label="Search brochures"
          placeholder="Search brochures..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, paddingLeft: 36 }}
        />
      </div>

      {filtered ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>
              No results for "{search}" in {stateName}.
            </div>
          )}
          {filtered.map((r) => (
            <RegCard key={r.id} reg={r} agencyName={agencyName} />
          ))}
        </div>
      ) : Object.keys(cats).length > 0 ? (
        Object.entries(cats).map(([cat, regs]) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <Label>{cat}</Label>
            <div className="stagger-children" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {regs.map((r) => (
                <RegCard key={r.id} reg={r} agencyName={agencyName} />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: "center", padding: 30, color: T.textDim }}>
          No regulations available for {stateName} yet.
        </div>
      )}
    </div>
  );
}

function RegCard({ reg, agencyName }) {
  return (
    <a href={reg.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
      <Card className="reg-card" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: T.blueDim, color: T.blue,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>{icon(I.Book, 18)}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: R.subSize, fontWeight: 600, color: T.text }}>{reg.name}</div>
          <div style={{ fontSize: R.smallSize, color: T.textDim }}>{agencyName || "PDF"}</div>
        </div>
        <div style={{ color: T.textDim }}>{icon(I.Link, 14)}</div>
      </Card>
    </a>
  );
}
